import React, { useState, useEffect, useRef, useCallback } from 'react';
import { FaHandshake, FaCheckCircle, FaClock, FaEdit, FaSpinner, FaClipboardList, FaBuilding, FaExclamationTriangle, FaPlus, FaCalendarAlt, FaList, FaChevronLeft, FaChevronRight, FaTimes, FaTrash, FaMoneyBillWave, FaHome, FaDollarSign, FaSearch, FaUserPlus, FaStore, FaFileAlt, FaLink, FaCheckSquare, FaPhone, FaEnvelope, FaExternalLinkAlt } from 'react-icons/fa';
import { getEmployeeDeals, updateDealProgress, getEmployeeUnitsWithListings } from '../utils/api';
import { ar, en } from '../utils/translations';
import { useToast, SuccessAnimation } from './Toast';

const EmployeeDashboard = ({ language = 'ar', userId, department }) => {
  const isMarketing = department === 'marketing';
  const t = language === 'ar' ? ar : en;
  const toast = useToast();
  const [showSuccess, setShowSuccess] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [deals, setDeals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingDeal, setEditingDeal] = useState(null);
  const [progress, setProgress] = useState(0);
  const [status, setStatus] = useState('pending');
  const [notes, setNotes] = useState('');

  // View mode (list or calendar)
  const [viewMode, setViewMode] = useState('list');
  const [selectedDate, setSelectedDate] = useState(new Date());
  
  // Internal tab state for organizing sections
  const [activeSection, setActiveSection] = useState('overview'); // 'overview', 'tasks', 'deals', 'units'
  
  // Marketing tasks state
  const [myTasks, setMyTasks] = useState([]);
  const [loadingTasks, setLoadingTasks] = useState(true);
  
  // Swipe state
  const [swipeStates, setSwipeStates] = useState({});
  
  // Ad closure tasks state (REGA compliance)
  const [closureTasks, setClosureTasks] = useState([]);
  const [loadingClosureTasks, setLoadingClosureTasks] = useState(true);
  const [closingAd, setClosingAd] = useState(null);
  const [showListingForm, setShowListingForm] = useState(null);
  const [listingFormData, setListingFormData] = useState({
    title: '',
    description: '',
    platform: 'بيوت',
    platform_other: '',
    price: '',
    external_url: '',
    operation_type: 'rent', // 'rent' or 'sale'
    client_phone: '',
    client_name: '',
    // REGA compliance fields
    commercial_registry: '',
    ad_license: '',
    platform_ad_number: '',
    has_brokerage_contract: false
  });
  const [searchingClient, setSearchingClient] = useState(false);
  const [clientSearchResults, setClientSearchResults] = useState([]);
  const [showNewClientModal, setShowNewClientModal] = useState(false);
  const [newClientData, setNewClientData] = useState({ name: '', phone: '', email: '' });
  const [creatingClient, setCreatingClient] = useState(false);
  const [submittingListing, setSubmittingListing] = useState(false);
  
  // Units with listings state (for deposit recording)
  const [unitsWithListings, setUnitsWithListings] = useState([]);
  const [loadingUnits, setLoadingUnits] = useState(false);
  const [showDepositModal, setShowDepositModal] = useState(false);
  const [selectedUnit, setSelectedUnit] = useState(null);
  const [depositFormData, setDepositFormData] = useState({
    client_name: '',
    client_phone: '',
    amount: '',
    payment_method: 'cash',
    receipt_preview: null
  });
          const [uploadingReceipt, setUploadingReceipt] = useState(false);
          
          // Deposit cancellation state
          const [showCancelDepositModal, setShowCancelDepositModal] = useState(false);
          const [cancellingDeposit, setCancellingDeposit] = useState(null);
          const [cancellationReason, setCancellationReason] = useState('');
          const [submittingCancellation, setSubmittingCancellation] = useState(false);
          
          // View listing details modal
          const [showViewListingModal, setShowViewListingModal] = useState(false);
          const [viewingListing, setViewingListing] = useState(null);
          const [editingListingUrl, setEditingListingUrl] = useState('');
          const [savingListingUrl, setSavingListingUrl] = useState(false);
          const [loadingListingDetails, setLoadingListingDetails] = useState(false);

  // Load ad closure tasks (listings pending closure after deposit)
  const loadClosureTasks = useCallback(async () => {
    try {
      setLoadingClosureTasks(true);
      const token = localStorage.getItem('token');
      const API_BASE = process.env.REACT_APP_API_URL || (process.env.NODE_ENV === 'production' ? '' : 'http://localhost:5001');
      
      // Get listings assigned to this user that need to be closed
      const res = await fetch(`${API_BASE}/api/listings?my_assignments=true&pending_closure=true`, {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (res.ok) {
        const data = await res.json();
        // Filter to only show listings that have pending close reminders
        const pendingClosure = data.filter(l => 
          l.status === 'ad_posted' && l.has_close_reminder
        );
        setClosureTasks(pendingClosure);
      } else {
        setClosureTasks([]);
      }
    } catch (error) {
      console.error('Error loading closure tasks:', error);
      setClosureTasks([]);
    } finally {
      setLoadingClosureTasks(false);
    }
  }, [userId, language, toast]);

  // Close an ad (REGA compliance)
  const handleCloseAd = async (listingId) => {
    if (!window.confirm(language === 'ar' 
      ? 'هل تأكدت من إزالة الإعلان من المنصة الخارجية؟' 
      : 'Have you removed the ad from the external platform?')) {
      return;
    }
    
    setClosingAd(listingId);
    try {
      const token = localStorage.getItem('token');
      const API_BASE = process.env.REACT_APP_API_URL || (process.env.NODE_ENV === 'production' ? '' : 'http://localhost:5001');
      
      const res = await fetch(`${API_BASE}/api/listings/${listingId}/close`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ close_reason: 'deposit_received' })
      });
      
      if (res.ok) {
        loadClosureTasks();
        setSuccessMessage(language === 'ar' 
          ? 'تم تأكيد إغلاق الإعلان - شكراً على الالتزام بأنظمة REGA' 
          : 'Ad closure confirmed - Thank you for REGA compliance');
        setShowSuccess(true);
      } else {
        throw new Error('Failed to close ad');
      }
    } catch (error) {
      console.error('Error closing ad:', error);
      toast.error(language === 'ar' ? 'فشل إغلاق الإعلان' : 'Failed to close ad');
    } finally {
      setClosingAd(null);
    }
  };

  // Open view listing details modal
  const openViewListingModal = async (listingId, unitId, listingData = null) => {
    setShowViewListingModal(true);
    
    // If listing data is already provided (from active_listing), use it directly
    if (listingData && listingData.id) {
      setViewingListing(listingData);
      setEditingListingUrl(listingData.external_url || '');
      setLoadingListingDetails(false);
      return;
    }
    
    // Otherwise, fetch from API
    setLoadingListingDetails(true);
    
    try {
      const token = localStorage.getItem('token');
      const API_BASE = process.env.REACT_APP_API_URL || (process.env.NODE_ENV === 'production' ? '' : 'http://localhost:5001');
      
      // Fetch listing details
      const response = await fetch(`${API_BASE}/api/listings?unit_id=${unitId}`, {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const listings = await response.json();
        // Find the specific listing or the most recent one for this unit
        const listing = listings.find(l => l.id === listingId) || listings.find(l => l.unit_id === unitId);
        
        if (listing) {
          setViewingListing(listing);
          setEditingListingUrl(listing.external_url || '');
        } else {
          toast.error(language === 'ar' ? 'لم يتم العثور على الإعلان' : 'Listing not found');
          setShowViewListingModal(false);
        }
      } else {
        throw new Error('Failed to fetch listing');
      }
    } catch (error) {
      console.error('Error fetching listing:', error);
      toast.error(language === 'ar' ? 'فشل تحميل تفاصيل الإعلان' : 'Failed to load listing details');
      setShowViewListingModal(false);
    } finally {
      setLoadingListingDetails(false);
    }
  };

  // Save listing URL
  const handleSaveListingUrl = async () => {
    if (!viewingListing) return;
    
    setSavingListingUrl(true);
    try {
      const token = localStorage.getItem('token');
      const API_BASE = process.env.REACT_APP_API_URL || (process.env.NODE_ENV === 'production' ? '' : 'http://localhost:5001');
      
      const response = await fetch(`${API_BASE}/api/listings/${viewingListing.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          external_url: editingListingUrl.trim() || null
        })
      });
      
      if (!response.ok) throw new Error('Failed to save URL');
      
      // Refresh data
      loadMyTasks();
      loadClosureTasks();
      if (isMarketing) {
        loadUnitsWithListings();
      }
      
      toast.success(language === 'ar' ? 'تم حفظ الرابط!' : 'URL saved!');
      setViewingListing({ ...viewingListing, external_url: editingListingUrl.trim() });
    } catch (error) {
      console.error('Error saving URL:', error);
      toast.error(language === 'ar' ? 'فشل حفظ الرابط' : 'Failed to save URL');
    } finally {
      setSavingListingUrl(false);
    }
  };

  // Load marketing tasks for this employee
  const loadMyTasks = useCallback(async () => {
    if (!isMarketing) return;
    
    try {
      setLoadingTasks(true);
      const token = localStorage.getItem('token');
      const API_BASE = process.env.REACT_APP_API_URL || (process.env.NODE_ENV === 'production' ? '' : 'http://localhost:5001');
      
      const res = await fetch(`${API_BASE}/api/marketing/tasks`, {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (res.ok) {
        const data = await res.json();
        setMyTasks(data);
      } else {
        setMyTasks([]);
      }
    } catch (error) {
      console.error('Error loading marketing tasks:', error);
      setMyTasks([]);
    } finally {
      setLoadingTasks(false);
    }
  }, [userId, isMarketing, language, toast]);

  // Search client by phone
  const searchClientByPhone = useCallback(async (phone) => {
    if (!phone || phone.length < 5) {
      setClientSearchResults([]);
      return;
    }
    
    setSearchingClient(true);
    try {
      const token = localStorage.getItem('token');
      const API_BASE = process.env.REACT_APP_API_URL || (process.env.NODE_ENV === 'production' ? '' : 'http://localhost:5001');
      
      // Use a search endpoint that works for employees (search by phone only)
      const phoneDigits = phone.replace(/\D/g, '');
      const res = await fetch(`${API_BASE}/api/clients/search?phone=${encodeURIComponent(phoneDigits)}`, {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (res.ok) {
        const data = await res.json();
        setClientSearchResults((data || []).slice(0, 5));
      } else if (res.status === 404) {
        // Endpoint doesn't exist, try the regular endpoint
        const res2 = await fetch(`${API_BASE}/api/clients?search=${encodeURIComponent(phone)}`, {
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          }
        });
        if (res2.ok) {
          const data = await res2.json();
          const filtered = (data || []).filter(c => 
            c.phone && c.phone.includes(phoneDigits)
          );
          setClientSearchResults(filtered.slice(0, 5));
        }
      }
    } catch (error) {
      console.error('Error searching client:', error);
      setClientSearchResults([]);
    } finally {
      setSearchingClient(false);
    }
  }, []);

  // Create new client
  const handleCreateClient = async () => {
    if (!newClientData.name || !newClientData.phone) {
      toast.warning(language === 'ar' ? 'يرجى إدخال الاسم ورقم الهاتف' : 'Please enter name and phone');
      return;
    }
    
    setCreatingClient(true);
    try {
      const token = localStorage.getItem('token');
      const API_BASE = process.env.REACT_APP_API_URL || (process.env.NODE_ENV === 'production' ? '' : 'http://localhost:5001');
      
      const res = await fetch(`${API_BASE}/api/clients`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          name: newClientData.name,
          phone: newClientData.phone,
          email: newClientData.email || null,
          customer_type: 'prospect',
          status: 'active'
        })
      });
      
      if (res.ok) {
        const client = await res.json();
        setListingFormData({
          ...listingFormData,
          client_phone: client.phone,
          client_name: client.name
        });
        setShowNewClientModal(false);
        setNewClientData({ name: '', phone: '', email: '' });
        toast.success(language === 'ar' ? 'تم إضافة العميل بنجاح' : 'Client added successfully');
      } else {
        const error = await res.json().catch(() => ({ error: 'Failed to create client' }));
        throw new Error(error.error || 'Failed to create client');
      }
    } catch (error) {
      console.error('Error creating client:', error);
      toast.error(error.message || (language === 'ar' ? 'فشل إضافة العميل' : 'Failed to create client'));
    } finally {
      setCreatingClient(false);
    }
  };

  // Open listing creation form
  const openListingForm = (task) => {
    const unitTypeAr = task.unit_type === 'apartment' ? 'شقة' : 
                       task.unit_type === 'villa' ? 'فيلا' : 
                       task.unit_type === 'office' ? 'مكتب' : 
                       task.unit_type === 'shop' ? 'محل' : 
                       task.unit_type === 'warehouse' ? 'مستودع' : 
                       task.unit_type || 'شقة';
    // Get saved commercial registry from localStorage
    const savedCommercialRegistry = localStorage.getItem('commercial_registry') || '';
    setListingFormData({
      title: task.unit_number ? `وحدة ${task.unit_number} - ${unitTypeAr}` : '',
      description: '',
      platform: 'بيوت',
      platform_other: '',
      price: task.monthly_rent || '',
      external_url: '',
      operation_type: 'rent',
      client_phone: '',
      client_name: '',
      commercial_registry: savedCommercialRegistry,
      ad_license: '',
      platform_ad_number: '',
      has_brokerage_contract: false
    });
    setClientSearchResults([]);
    setShowListingForm(task);
  };

  // Handle listing creation
  const handleCreateListing = async () => {
    if (!showListingForm) return;
    
    setSubmittingListing(true);
    try {
      const token = localStorage.getItem('token');
      const API_BASE = process.env.REACT_APP_API_URL || (process.env.NODE_ENV === 'production' ? '' : 'http://localhost:5001');
      
      const res = await fetch(`${API_BASE}/api/marketing/tasks/${showListingForm.id}/create-listing`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(listingFormData)
      });
      
      if (res.ok) {
        const result = await res.json();
        setShowListingForm(null);
        loadMyTasks();
        // Refresh units with listings to get updated client info
        if (isMarketing) {
          loadUnitsWithListings();
        }
        setSuccessMessage(language === 'ar' 
          ? `تم إنشاء الإعلان بنجاح! ${result.sla_met ? '✅ ضمن المهلة' : '⚠️ تجاوز المهلة'}`
          : `Listing created! ${result.sla_met ? '✅ Within SLA' : '⚠️ SLA exceeded'}`);
        setShowSuccess(true);
      } else {
        throw new Error('Failed to create listing');
      }
    } catch (error) {
      console.error('Error creating listing:', error);
      toast.error(language === 'ar' ? 'فشل إنشاء الإعلان' : 'Failed to create listing');
    } finally {
      setSubmittingListing(false);
    }
  };

  const loadDeals = useCallback(async () => {
    if (!userId) {
      setLoading(false);
      return;
    }
    
    try {
      setLoading(true);
      const response = await getEmployeeDeals(userId);
      setDeals(response.data || []);
    } catch (error) {
      console.error('Error loading deals:', error);
      setDeals([]); // Set empty array on error
      if (error.response?.status === 403) {
        toast.error(language === 'ar' ? 'ليس لديك صلاحية لعرض الصفقات' : 'You do not have permission to view deals');
      }
    } finally {
      setLoading(false);
    }
  }, [userId, language, toast]);

  // Load units with listings assigned to this employee (for deposit recording)
  const loadUnitsWithListings = useCallback(async () => {
    if (!isMarketing) return; // Only for marketing employees
    
    try {
      setLoadingUnits(true);
      const response = await getEmployeeUnitsWithListings(userId);
      setUnitsWithListings(response.data || []);
    } catch (error) {
      console.error('Error loading units with listings:', error);
      setUnitsWithListings([]);
    } finally {
      setLoadingUnits(false);
    }
  }, [userId, isMarketing]);

  // Load data when userId becomes available
  useEffect(() => {
    if (!userId) {
      console.log('⏳ EmployeeDashboard: Waiting for userId...');
      setLoading(false);
      return;
    }
    
    console.log('✅ EmployeeDashboard: Loading data for userId', userId);
    
    // Load data immediately when userId is available
    const loadData = async () => {
      try {
        console.log('📊 Loading deals...');
        await loadDeals();
        if (isMarketing) {
          console.log('📋 Loading marketing tasks...');
          await loadMyTasks();
          console.log('🔒 Loading closure tasks...');
          await loadClosureTasks();
          console.log('🏠 Loading units with listings...');
          await loadUnitsWithListings();
        }
        console.log('✅ All data loaded successfully');
      } catch (error) {
        console.error('❌ Error loading dashboard data:', error);
        setLoading(false); // Ensure loading state is cleared on error
      }
    };
    
    // Load immediately
    loadData();
  }, [userId, isMarketing, loadDeals, loadMyTasks, loadClosureTasks, loadUnitsWithListings]);

  // Auto-refresh every 30 seconds when tab is visible
  useEffect(() => {
    if (!userId) return;
    
    const interval = setInterval(() => {
      if (document.visibilityState === 'visible') {
        loadDeals();
        if (isMarketing) {
          loadMyTasks();
          loadClosureTasks();
          loadUnitsWithListings();
        }
      }
    }, 30000);

    return () => clearInterval(interval);
  }, [userId, isMarketing, loadDeals, loadMyTasks, loadClosureTasks, loadUnitsWithListings]);

  const handleEdit = (deal) => {
    setEditingDeal(deal);
    setProgress(deal.progress || 0);
    setStatus(deal.status || 'pending');
    setNotes(deal.progress_notes || '');
  };

  const handleSave = async () => {
    if (!editingDeal) return;
    
    try {
      await updateDealProgress(editingDeal.id, {
        progress: parseInt(progress),
        status,
        notes
      });
      toast.success(language === 'ar' ? '✅ تم تحديث التقدم بنجاح' : '✅ Progress updated successfully');
      setEditingDeal(null);
      loadDeals();
    } catch (error) {
      console.error('Error updating progress:', error);
      toast.error(language === 'ar' ? 'فشل تحديث التقدم' : 'Failed to update progress');
    }
  };

  // Deposit recording functions
  const openDepositModal = (unit) => {
    setSelectedUnit(unit);
    // Pre-populate client info from listing if available
    // Check both active_listing object and direct listing properties
    const listingClientName = unit.active_listing?.client_name || unit.listing_client_name || '';
    const listingClientPhone = unit.active_listing?.client_phone || unit.listing_client_phone || '';
    setDepositFormData({
      client_name: listingClientName,
      client_phone: listingClientPhone,
      amount: unit.monthly_rent || '',
      payment_method: 'cash',
      receipt_preview: null
    });
    setShowDepositModal(true);
  };

  const handleReceiptUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setDepositFormData({...depositFormData, receipt_preview: reader.result});
      };
      reader.readAsDataURL(file);
    }
  };

  const handleDepositSubmit = async (e) => {
    e.preventDefault();
    
    if (!depositFormData.receipt_preview) {
      toast.warning(language === 'ar' ? 'يرجى إرفاق صورة الإيصال' : 'Please attach receipt image');
      return;
    }
    
    setUploadingReceipt(true);
    
    try {
      const token = localStorage.getItem('token');
      const API_BASE = process.env.REACT_APP_API_URL || (process.env.NODE_ENV === 'production' ? '' : 'http://localhost:5001');
      
      const response = await fetch(`${API_BASE}/api/deposits`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          unit_id: selectedUnit.id,
          client_name: depositFormData.client_name,
          client_phone: depositFormData.client_phone,
          amount: depositFormData.amount,
          payment_method: depositFormData.payment_method,
          receipt_image: depositFormData.receipt_preview
        })
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Failed to record deposit' }));
        throw new Error(errorData.error || 'Failed to record deposit');
      }
      
      const result = await response.json();
      setShowDepositModal(false);
      setSelectedUnit(null);
      setDepositFormData({
        client_name: '',
        client_phone: '',
        amount: '',
        payment_method: 'cash',
        receipt_preview: null
      });
      
      // Reload units
      loadUnitsWithListings();
      
      toast.success(language === 'ar' 
        ? '✅ تم تسجيل العربون! تم حجز الوحدة وإنشاء تذكير لإزالة الإعلان.'
        : '✅ Deposit recorded! Unit reserved and reminder created.');
    } catch (error) {
      console.error('Error recording deposit:', error);
      toast.error(error.message || (language === 'ar' ? 'فشل في تسجيل العربون' : 'Failed to record deposit'));
    } finally {
      setUploadingReceipt(false);
    }
  };

  // Request deposit cancellation
  const handleRequestCancellation = async (e) => {
    e.preventDefault();
    
    if (!cancellationReason || cancellationReason.trim().length < 10) {
      toast.warning(language === 'ar' 
        ? 'يرجى كتابة سبب الإلغاء (10 أحرف على الأقل)' 
        : 'Please provide cancellation reason (minimum 10 characters)');
      return;
    }
    
    setSubmittingCancellation(true);
    try {
      const token = localStorage.getItem('token');
      const API_BASE = process.env.REACT_APP_API_URL || (process.env.NODE_ENV === 'production' ? '' : 'http://localhost:5001');
      
      const response = await fetch(`${API_BASE}/api/deposits/${cancellingDeposit.id}/request-cancel`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ reason: cancellationReason.trim() })
      });
      
      const result = await response.json().catch(() => ({ error: 'Failed to request cancellation' }));
      
      if (response.ok) {
        toast.success(language === 'ar' 
          ? '✅ تم إرسال طلب الإلغاء للموافقة' 
          : '✅ Cancellation request submitted for approval');
        setShowCancelDepositModal(false);
        setCancellingDeposit(null);
        setCancellationReason('');
        loadUnitsWithListings();
      } else {
        toast.error(result.error || 'Failed to request cancellation');
      }
    } catch (error) {
      console.error('Error requesting cancellation:', error);
      toast.error(error.message || (language === 'ar' ? 'فشل في طلب الإلغاء' : 'Failed to request cancellation'));
    } finally {
      setSubmittingCancellation(false);
    }
  };

  // Open cancel deposit modal
  const openCancelDepositModal = (deposit, unit) => {
    setCancellingDeposit({ ...deposit, unit_number: unit.unit_number });
    setCancellationReason('');
    setShowCancelDepositModal(true);
  };

  // Calculate task progress based on status and time
  const getTaskProgress = (task) => {
    if (task.completed) return 100;
    if (task.listing_created) return 75;
    if (task.hours_until_deadline && task.hours_until_deadline < 24) return 50;
    return 25; // Task assigned
  };

  const getProgressColor = (progress) => {
    if (progress >= 75) return '#10b981';
    if (progress >= 50) return '#3b82f6';
    if (progress >= 25) return '#f59e0b';
    return '#6b7280';
  };

  // Swipe handlers for mobile
  const handleTouchStart = (taskId, e) => {
    const touch = e.touches[0];
    setSwipeStates(prev => ({
      ...prev,
      [taskId]: { startX: touch.clientX, currentX: touch.clientX, swiping: true }
    }));
  };

  const handleTouchMove = (taskId, e) => {
    if (!swipeStates[taskId]?.swiping) return;
    const touch = e.touches[0];
    setSwipeStates(prev => ({
      ...prev,
      [taskId]: { ...prev[taskId], currentX: touch.clientX }
    }));
  };

  const handleTouchEnd = (taskId, task) => {
    const state = swipeStates[taskId];
    if (!state) return;
    
    const diff = state.startX - state.currentX;
    const threshold = 80;
    
    if (diff > threshold) {
      // Swiped left - Open listing form
      openListingForm(task);
    } else if (diff < -threshold) {
      // Swiped right - Mark as done (if applicable)
      // For now, just reset
    }
    
    setSwipeStates(prev => ({
      ...prev,
      [taskId]: { startX: 0, currentX: 0, swiping: false }
    }));
  };

  const getSwipeOffset = (taskId) => {
    const state = swipeStates[taskId];
    if (!state?.swiping) return 0;
    return Math.max(-100, Math.min(100, state.startX - state.currentX));
  };

  // Calendar helpers
  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const days = [];
    
    // Add empty slots for days before first day
    for (let i = 0; i < firstDay.getDay(); i++) {
      days.push(null);
    }
    
    // Add actual days
    for (let i = 1; i <= lastDay.getDate(); i++) {
      days.push(new Date(year, month, i));
    }
    
    return days;
  };

  const getTasksForDate = (date) => {
    if (!date) return [];
    return myTasks.filter(task => {
      const deadline = task.sla_deadline ? new Date(task.sla_deadline) : null;
      if (!deadline) return false;
      return deadline.toDateString() === date.toDateString();
    });
  };

  const getClosureTasksForDate = (date) => {
    if (!date) return [];
    return closureTasks.filter(task => {
      let deadline = null;
      try {
        const notes = JSON.parse(task.close_reminder_notes || '{}');
        deadline = notes.rega_deadline ? new Date(notes.rega_deadline) : null;
      } catch (e) {}
      if (!deadline) return false;
      return deadline.toDateString() === date.toDateString();
    });
  };

  const prevMonth = () => {
    setSelectedDate(new Date(selectedDate.getFullYear(), selectedDate.getMonth() - 1, 1));
  };

  const nextMonth = () => {
    setSelectedDate(new Date(selectedDate.getFullYear(), selectedDate.getMonth() + 1, 1));
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return '#10b981';
      case 'in_progress': return '#3b82f6';
      case 'on_hold': return '#f59e0b';
      case 'pending': return '#6b7280';
      default: return '#6b7280';
    }
  };

  const getStatusLabel = (status) => {
    const labels = {
      pending: { ar: 'قيد الانتظار', en: 'Pending' },
      in_progress: { ar: 'قيد التنفيذ', en: 'In Progress' },
      on_hold: { ar: 'معلق', en: 'On Hold' },
      completed: { ar: 'مكتمل', en: 'Completed' }
    };
    return labels[status]?.[language] || status;
  };

  if (loading) {
    return (
      <div className="card" style={{ textAlign: 'center', padding: '40px' }}>
        <FaSpinner style={{ fontSize: '2rem', color: '#f97316', animation: 'spin 1s linear infinite' }} />
        <p style={{ marginTop: '20px' }}>{language === 'ar' ? 'جاري التحميل...' : 'Loading...'}</p>
      </div>
    );
  }

  // Calculate KPIs
  const totalTasks = myTasks.length;
  const pendingTasks = myTasks.filter(t => !t.listing_created && !t.completed).length;
  const completedTasks = myTasks.filter(t => t.completed || t.listing_created).length;
  const overdueTasks = myTasks.filter(t => t.is_overdue).length;
  const totalDeals = deals.length;
  const activeDeals = deals.filter(d => d.status === 'in_progress').length;
  const totalUnits = unitsWithListings.length;
  const unitsWithDeposits = unitsWithListings.filter(u => u.deposit).length;
  const pendingClosures = closureTasks.length;

  return (
    <div>
      {/* Header with KPIs */}
      <div className="card" style={{ marginBottom: '25px', padding: '20px' }}>
        <h2 style={{ margin: '0 0 20px 0', fontSize: '1.5rem', fontWeight: '700', color: '#1f2937' }}>
          {language === 'ar' ? 'لوحة التحكم' : 'Control Panel'}
        </h2>
        
        {/* KPI Cards */}
        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', 
          gap: '18px',
          marginBottom: '20px'
        }}>
          {isMarketing && (
            <>
              {/* Total Tasks - Warm Orange Gradient */}
              <div 
                onClick={() => setActiveSection('tasks')}
                style={{
                  padding: '26px',
                  background: 'linear-gradient(135deg, #fff4e0 0%, #ffe5cc 50%, #ffd4a3 100%)',
                  borderRadius: '18px',
                  border: '3px solid #f97316',
                  textAlign: 'center',
                  cursor: 'pointer',
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  boxShadow: '0 6px 16px rgba(249, 115, 22, 0.2), inset 0 1px 0 rgba(255, 255, 255, 0.5)',
                  position: 'relative',
                  overflow: 'hidden'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-6px) scale(1.02)';
                  e.currentTarget.style.boxShadow = '0 12px 28px rgba(249, 115, 22, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.6)';
                  e.currentTarget.style.borderColor = '#ea580c';
                  e.currentTarget.style.background = 'linear-gradient(135deg, #ffe5cc 0%, #ffd4a3 50%, #ffb84d 100%)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0) scale(1)';
                  e.currentTarget.style.boxShadow = '0 6px 16px rgba(249, 115, 22, 0.2), inset 0 1px 0 rgba(255, 255, 255, 0.5)';
                  e.currentTarget.style.borderColor = '#f97316';
                  e.currentTarget.style.background = 'linear-gradient(135deg, #fff4e0 0%, #ffe5cc 50%, #ffd4a3 100%)';
                }}
              >
                <div style={{ 
                  position: 'absolute', 
                  top: '-10px', 
                  right: '-10px', 
                  opacity: 0.15,
                  fontSize: '4rem',
                  color: '#f97316',
                  transform: 'rotate(-15deg)'
                }}>
                  <FaClipboardList />
                </div>
                <div style={{ fontSize: '2.8rem', fontWeight: '800', color: '#ea580c', marginBottom: '10px', position: 'relative', textShadow: '0 2px 4px rgba(234, 88, 12, 0.2)' }}>
                  {totalTasks}
                </div>
                <div style={{ fontSize: '1rem', color: '#9a3412', fontWeight: '700', position: 'relative', letterSpacing: '0.3px' }}>
                  {language === 'ar' ? 'إجمالي المهام' : 'Total Tasks'}
                </div>
              </div>
              
              {/* Pending Tasks - Soft Amber */}
              <div 
                onClick={() => setActiveSection('tasks')}
                style={{
                  padding: '26px',
                  background: 'linear-gradient(135deg, #fff7ed 0%, #fff4e0 50%, #ffecc8 100%)',
                  borderRadius: '18px',
                  border: '3px solid #fdba74',
                  textAlign: 'center',
                  cursor: 'pointer',
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  boxShadow: '0 6px 16px rgba(253, 186, 116, 0.2), inset 0 1px 0 rgba(255, 255, 255, 0.5)',
                  position: 'relative',
                  overflow: 'hidden'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-6px) scale(1.02)';
                  e.currentTarget.style.boxShadow = '0 12px 28px rgba(253, 186, 116, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.6)';
                  e.currentTarget.style.borderColor = '#f97316';
                  e.currentTarget.style.background = 'linear-gradient(135deg, #fff4e0 0%, #ffecc8 50%, #ffd4a3 100%)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0) scale(1)';
                  e.currentTarget.style.boxShadow = '0 6px 16px rgba(253, 186, 116, 0.2), inset 0 1px 0 rgba(255, 255, 255, 0.5)';
                  e.currentTarget.style.borderColor = '#fdba74';
                  e.currentTarget.style.background = 'linear-gradient(135deg, #fff7ed 0%, #fff4e0 50%, #ffecc8 100%)';
                }}
              >
                <div style={{ 
                  position: 'absolute', 
                  top: '-10px', 
                  right: '-10px', 
                  opacity: 0.15,
                  fontSize: '4rem',
                  color: '#f97316',
                  transform: 'rotate(-15deg)'
                }}>
                  <FaClock />
                </div>
                <div style={{ fontSize: '2.8rem', fontWeight: '800', color: '#f97316', marginBottom: '10px', position: 'relative', textShadow: '0 2px 4px rgba(249, 115, 22, 0.2)' }}>
                  {pendingTasks}
                </div>
                <div style={{ fontSize: '1rem', color: '#9a3412', fontWeight: '700', position: 'relative', letterSpacing: '0.3px' }}>
                  {language === 'ar' ? 'مهام قيد التنفيذ' : 'Pending Tasks'}
                </div>
              </div>
              
              {/* Completed Tasks - Success Orange */}
              <div 
                onClick={() => setActiveSection('tasks')}
                style={{
                  padding: '26px',
                  background: 'linear-gradient(135deg, #fff8ed 0%, #fff4e0 50%, #ffe5cc 100%)',
                  borderRadius: '18px',
                  border: '3px solid #f97316',
                  textAlign: 'center',
                  cursor: 'pointer',
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  boxShadow: '0 6px 16px rgba(249, 115, 22, 0.2), inset 0 1px 0 rgba(255, 255, 255, 0.5)',
                  position: 'relative',
                  overflow: 'hidden'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-6px) scale(1.02)';
                  e.currentTarget.style.boxShadow = '0 12px 28px rgba(249, 115, 22, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.6)';
                  e.currentTarget.style.borderColor = '#ea580c';
                  e.currentTarget.style.background = 'linear-gradient(135deg, #fff4e0 0%, #ffe5cc 50%, #ffd4a3 100%)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0) scale(1)';
                  e.currentTarget.style.boxShadow = '0 6px 16px rgba(249, 115, 22, 0.2), inset 0 1px 0 rgba(255, 255, 255, 0.5)';
                  e.currentTarget.style.borderColor = '#f97316';
                  e.currentTarget.style.background = 'linear-gradient(135deg, #fff8ed 0%, #fff4e0 50%, #ffe5cc 100%)';
                }}
              >
                <div style={{ 
                  position: 'absolute', 
                  top: '-10px', 
                  right: '-10px', 
                  opacity: 0.15,
                  fontSize: '4rem',
                  color: '#f97316',
                  transform: 'rotate(-15deg)'
                }}>
                  <FaCheckCircle />
                </div>
                <div style={{ fontSize: '2.8rem', fontWeight: '800', color: '#ea580c', marginBottom: '10px', position: 'relative', textShadow: '0 2px 4px rgba(234, 88, 12, 0.2)' }}>
                  {completedTasks}
                </div>
                <div style={{ fontSize: '1rem', color: '#9a3412', fontWeight: '700', position: 'relative', letterSpacing: '0.3px' }}>
                  {language === 'ar' ? 'مهام مكتملة' : 'Completed'}
                </div>
              </div>
              
              {/* Overdue Tasks - Alert Orange */}
              {overdueTasks > 0 && (
                <div 
                  onClick={() => setActiveSection('tasks')}
                  style={{
                    padding: '26px',
                    background: 'linear-gradient(135deg, #fff4e6 0%, #ffe5cc 50%, #ffb84d 100%)',
                    borderRadius: '18px',
                    border: '3px solid #ea580c',
                    textAlign: 'center',
                    cursor: 'pointer',
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    boxShadow: '0 6px 16px rgba(234, 88, 12, 0.25), inset 0 1px 0 rgba(255, 255, 255, 0.5)',
                    position: 'relative',
                    overflow: 'hidden',
                    animation: overdueTasks > 0 ? 'pulse 2s infinite' : 'none'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-6px) scale(1.05)';
                    e.currentTarget.style.boxShadow = '0 12px 28px rgba(234, 88, 12, 0.4), inset 0 1px 0 rgba(255, 255, 255, 0.6)';
                    e.currentTarget.style.borderColor = '#c2410c';
                    e.currentTarget.style.background = 'linear-gradient(135deg, #ffe5cc 0%, #ffb84d 50%, #ff9500 100%)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0) scale(1)';
                    e.currentTarget.style.boxShadow = '0 6px 16px rgba(234, 88, 12, 0.25), inset 0 1px 0 rgba(255, 255, 255, 0.5)';
                    e.currentTarget.style.borderColor = '#ea580c';
                    e.currentTarget.style.background = 'linear-gradient(135deg, #fff4e6 0%, #ffe5cc 50%, #ffb84d 100%)';
                  }}
                >
                  <div style={{ 
                    position: 'absolute', 
                    top: '-10px', 
                    right: '-10px', 
                    opacity: 0.2,
                    fontSize: '4rem',
                    color: '#ea580c',
                    transform: 'rotate(-15deg)',
                    animation: 'pulse 2s infinite'
                  }}>
                    <FaExclamationTriangle />
                  </div>
                  <div style={{ fontSize: '2.8rem', fontWeight: '800', color: '#c2410c', marginBottom: '10px', position: 'relative', textShadow: '0 2px 4px rgba(194, 65, 12, 0.3)' }}>
                    {overdueTasks}
                  </div>
                  <div style={{ fontSize: '1rem', color: '#9a3412', fontWeight: '700', position: 'relative', letterSpacing: '0.3px' }}>
                    {language === 'ar' ? 'مهام متأخرة' : 'Overdue'}
                  </div>
                </div>
              )}
            </>
          )}
          
          {/* Total Deals - Rich Orange */}
          <div 
            onClick={() => setActiveSection('deals')}
            style={{
              padding: '26px',
              background: 'linear-gradient(135deg, #fff4e0 0%, #ffe5cc 50%, #ffd4a3 100%)',
              borderRadius: '18px',
              border: '3px solid #f97316',
              textAlign: 'center',
              cursor: 'pointer',
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              boxShadow: '0 6px 16px rgba(249, 115, 22, 0.2), inset 0 1px 0 rgba(255, 255, 255, 0.5)',
              position: 'relative',
              overflow: 'hidden'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-6px) scale(1.02)';
              e.currentTarget.style.boxShadow = '0 12px 28px rgba(249, 115, 22, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.6)';
              e.currentTarget.style.borderColor = '#ea580c';
              e.currentTarget.style.background = 'linear-gradient(135deg, #ffe5cc 0%, #ffd4a3 50%, #ffb84d 100%)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0) scale(1)';
              e.currentTarget.style.boxShadow = '0 6px 16px rgba(249, 115, 22, 0.2), inset 0 1px 0 rgba(255, 255, 255, 0.5)';
              e.currentTarget.style.borderColor = '#f97316';
              e.currentTarget.style.background = 'linear-gradient(135deg, #fff4e0 0%, #ffe5cc 50%, #ffd4a3 100%)';
            }}
          >
            <div style={{ 
              position: 'absolute', 
              top: '-10px', 
              right: '-10px', 
              opacity: 0.15,
              fontSize: '4rem',
              color: '#f97316',
              transform: 'rotate(-15deg)'
            }}>
              <FaHandshake />
            </div>
            <div style={{ fontSize: '2.8rem', fontWeight: '800', color: '#ea580c', marginBottom: '10px', position: 'relative', textShadow: '0 2px 4px rgba(234, 88, 12, 0.2)' }}>
              {totalDeals}
            </div>
            <div style={{ fontSize: '1rem', color: '#9a3412', fontWeight: '700', position: 'relative', letterSpacing: '0.3px' }}>
              {language === 'ar' ? 'إجمالي الصفقات' : 'Total Deals'}
            </div>
          </div>
          
          {/* Units with Listings - Cream Orange */}
          {isMarketing && totalUnits > 0 && (
            <div 
              onClick={() => setActiveSection('units')}
              style={{
                padding: '26px',
                background: 'linear-gradient(135deg, #fff7ed 0%, #fff4e0 50%, #ffecc8 100%)',
                borderRadius: '18px',
                border: '3px solid #fdba74',
                textAlign: 'center',
                cursor: 'pointer',
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                boxShadow: '0 6px 16px rgba(253, 186, 116, 0.2), inset 0 1px 0 rgba(255, 255, 255, 0.5)',
                position: 'relative',
                overflow: 'hidden'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-6px) scale(1.02)';
                e.currentTarget.style.boxShadow = '0 12px 28px rgba(253, 186, 116, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.6)';
                e.currentTarget.style.borderColor = '#f97316';
                e.currentTarget.style.background = 'linear-gradient(135deg, #fff4e0 0%, #ffecc8 50%, #ffd4a3 100%)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0) scale(1)';
                e.currentTarget.style.boxShadow = '0 6px 16px rgba(253, 186, 116, 0.2), inset 0 1px 0 rgba(255, 255, 255, 0.5)';
                e.currentTarget.style.borderColor = '#fdba74';
                e.currentTarget.style.background = 'linear-gradient(135deg, #fff7ed 0%, #fff4e0 50%, #ffecc8 100%)';
              }}
            >
              <div style={{ 
                position: 'absolute', 
                top: '-10px', 
                right: '-10px', 
                opacity: 0.15,
                fontSize: '4rem',
                color: '#f97316',
                transform: 'rotate(-15deg)'
              }}>
                <FaHome />
              </div>
              <div style={{ fontSize: '2.8rem', fontWeight: '800', color: '#f97316', marginBottom: '10px', position: 'relative', textShadow: '0 2px 4px rgba(249, 115, 22, 0.2)' }}>
                {totalUnits}
              </div>
              <div style={{ fontSize: '1rem', color: '#9a3412', fontWeight: '700', position: 'relative', letterSpacing: '0.3px' }}>
                {language === 'ar' ? 'وحدات مع إعلانات' : 'Units w/ Listings'}
              </div>
            </div>
          )}
        </div>
        
        <style>
          {`
            @keyframes pulse {
              0%, 100% {
                opacity: 1;
                transform: scale(1);
              }
              50% {
                opacity: 0.9;
                transform: scale(1.01);
              }
            }
          `}
        </style>

        {/* Section Tabs */}
        <div style={{
          display: 'flex',
          gap: '10px',
          borderBottom: '2px solid #e5e7eb',
          marginBottom: '20px',
          flexWrap: 'wrap'
        }}>
          <button
            onClick={() => setActiveSection('overview')}
            style={{
              padding: '12px 20px',
              border: 'none',
              background: 'transparent',
              borderBottom: activeSection === 'overview' ? '3px solid #f97316' : '3px solid transparent',
              color: activeSection === 'overview' ? '#f97316' : '#6b7280',
              fontWeight: activeSection === 'overview' ? '600' : '500',
              cursor: 'pointer',
              fontSize: '0.95rem',
              transition: 'all 0.2s'
            }}
          >
            {language === 'ar' ? 'نظرة عامة' : 'Overview'}
          </button>
          
          {isMarketing && (
            <button
              onClick={() => setActiveSection('tasks')}
              style={{
                padding: '12px 20px',
                border: 'none',
                background: 'transparent',
                borderBottom: activeSection === 'tasks' ? '3px solid #f97316' : '3px solid transparent',
                color: activeSection === 'tasks' ? '#f97316' : '#6b7280',
                fontWeight: activeSection === 'tasks' ? '600' : '500',
                cursor: 'pointer',
                fontSize: '0.95rem',
                transition: 'all 0.2s',
                display: 'flex',
                alignItems: 'center',
                gap: '6px'
              }}
            >
              <FaClipboardList />
              {language === 'ar' ? 'المهام' : 'Tasks'}
              {pendingTasks > 0 && (
                <span style={{
                  background: '#f97316',
                  color: 'white',
                  borderRadius: '12px',
                  padding: '2px 8px',
                  fontSize: '0.75rem',
                  fontWeight: '600'
                }}>
                  {pendingTasks}
                </span>
              )}
            </button>
          )}
          
          <button
            onClick={() => setActiveSection('deals')}
            style={{
              padding: '12px 20px',
              border: 'none',
              background: 'transparent',
              borderBottom: activeSection === 'deals' ? '3px solid #f97316' : '3px solid transparent',
              color: activeSection === 'deals' ? '#f97316' : '#6b7280',
              fontWeight: activeSection === 'deals' ? '600' : '500',
              cursor: 'pointer',
              fontSize: '0.95rem',
              transition: 'all 0.2s',
              display: 'flex',
              alignItems: 'center',
              gap: '6px'
            }}
          >
            <FaHandshake />
            {language === 'ar' ? 'الصفقات' : 'Deals'}
            {totalDeals > 0 && (
              <span style={{
                background: '#f97316',
                color: 'white',
                borderRadius: '12px',
                padding: '2px 8px',
                fontSize: '0.75rem',
                fontWeight: '600'
              }}>
                {totalDeals}
              </span>
            )}
          </button>
          
          {isMarketing && totalUnits > 0 && (
            <button
              onClick={() => setActiveSection('units')}
              style={{
                padding: '12px 20px',
                border: 'none',
                background: 'transparent',
                borderBottom: activeSection === 'units' ? '3px solid #f97316' : '3px solid transparent',
                color: activeSection === 'units' ? '#f97316' : '#6b7280',
                fontWeight: activeSection === 'units' ? '600' : '500',
                cursor: 'pointer',
                fontSize: '0.95rem',
                transition: 'all 0.2s',
                display: 'flex',
                alignItems: 'center',
                gap: '6px'
              }}
            >
              <FaHome />
              {language === 'ar' ? 'الوحدات' : 'Units'}
              {totalUnits > 0 && (
                <span style={{
                  background: '#f97316',
                  color: 'white',
                  borderRadius: '12px',
                  padding: '2px 8px',
                  fontSize: '0.75rem',
                  fontWeight: '600'
                }}>
                  {totalUnits}
                </span>
              )}
            </button>
          )}
        </div>
      </div>

      {/* Content Sections */}
      {/* Overview Section */}
      {activeSection === 'overview' && (
        <div>
          {/* Quick Summary */}
          {isMarketing && (
            <div className="card" style={{ marginBottom: '20px', padding: '20px' }}>
              <h3 style={{ margin: '0 0 15px 0', fontSize: '1.2rem', fontWeight: '600' }}>
                {language === 'ar' ? 'ملخص سريع' : 'Quick Summary'}
              </h3>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '15px' }}>
                <div style={{ padding: '15px', background: '#f9fafb', borderRadius: '10px' }}>
                  <div style={{ fontSize: '0.85rem', color: '#6b7280', marginBottom: '5px' }}>
                    {language === 'ar' ? 'مهام تحتاج إغلاق' : 'Tasks Needing Closure'}
                  </div>
                  <div style={{ fontSize: '1.5rem', fontWeight: '700', color: '#dc2626' }}>
                    {pendingClosures}
                  </div>
                </div>
                <div style={{ padding: '15px', background: '#f9fafb', borderRadius: '10px' }}>
                  <div style={{ fontSize: '0.85rem', color: '#6b7280', marginBottom: '5px' }}>
                    {language === 'ar' ? 'وحدات مع عربون' : 'Units with Deposits'}
                  </div>
                  <div style={{ fontSize: '1.5rem', fontWeight: '700', color: '#059669' }}>
                    {unitsWithDeposits}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Marketing Tasks Section - Only for Marketing Team */}
      {(activeSection === 'tasks' || activeSection === 'overview') && isMarketing && (
      <div style={{
        marginBottom: '25px',
        padding: '20px',
        background: 'linear-gradient(135deg, #fef3c7 0%, #fffbeb 100%)',
        borderRadius: '16px',
        border: '2px solid #fcd34d'
      }}>
        <div style={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center', 
          marginBottom: '15px',
          flexWrap: 'wrap',
          gap: '10px'
        }}>
          <h3 style={{ 
            margin: 0, 
            display: 'flex', 
            alignItems: 'center', 
            gap: '10px',
            fontSize: '1.1rem'
          }}>
            <FaClipboardList style={{ color: '#d97706' }} />
            {language === 'ar' ? 'مهام التسويق' : 'Marketing Tasks'}
            {loadingTasks && (
              <span style={{ fontSize: '0.7rem', opacity: 0.6 }}>
                ({language === 'ar' ? 'جاري التحميل...' : 'loading...'})
              </span>
            )}
          </h3>
          
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            {/* View Toggle */}
            <div style={{ 
              display: 'flex', 
              background: 'white', 
              borderRadius: '8px', 
              overflow: 'hidden',
              border: '1px solid #fcd34d'
            }}>
              <button
                onClick={() => setViewMode('list')}
                style={{
                  padding: '6px 12px',
                  border: 'none',
                  background: viewMode === 'list' ? '#d97706' : 'white',
                  color: viewMode === 'list' ? 'white' : '#d97706',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px',
                  fontSize: '0.85rem'
                }}
              >
                <FaList />
              </button>
              <button
                onClick={() => setViewMode('calendar')}
                style={{
                  padding: '6px 12px',
                  border: 'none',
                  background: viewMode === 'calendar' ? '#d97706' : 'white',
                  color: viewMode === 'calendar' ? 'white' : '#d97706',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px',
                  fontSize: '0.85rem'
                }}
              >
                <FaCalendarAlt />
              </button>
            </div>
            
            <span style={{
              padding: '4px 12px',
              background: '#d97706',
              color: 'white',
              borderRadius: '20px',
              fontSize: '0.85rem',
              fontWeight: '600'
            }}>
              {myTasks.length} {language === 'ar' ? 'مهمة' : 'tasks'}
            </span>
          </div>
        </div>

        {/* Swipe hint for mobile */}
        <div style={{ 
          display: 'none', 
          fontSize: '0.75rem', 
          color: '#92400e', 
          marginBottom: '10px',
          textAlign: 'center'
        }} className="swipe-hint">
          {language === 'ar' ? '← اسحب لإنشاء إعلان' : '← Swipe left to create listing'}
        </div>
        <style>
          {`@media (max-width: 768px) {
            .swipe-hint { display: block !important; }
          }`}
        </style>

        {myTasks.length === 0 ? (
          <div style={{ 
            textAlign: 'center', 
            padding: '30px', 
            color: '#6b7280',
            background: 'white',
            borderRadius: '12px'
          }}>
            <FaCheckCircle style={{ fontSize: '32px', color: '#16a34a', marginBottom: '10px' }} />
            <div>{language === 'ar' ? 'لا توجد مهام حالياً - أحسنت!' : 'No tasks currently - Great job!'}</div>
          </div>
        ) : viewMode === 'calendar' ? (
          /* Calendar View */
          <div style={{ background: 'white', borderRadius: '12px', padding: '15px' }}>
            {/* Calendar Header */}
            <div style={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center', 
              marginBottom: '15px' 
            }}>
              <button 
                onClick={prevMonth}
                style={{ 
                  background: '#f3f4f6', 
                  border: 'none', 
                  padding: '8px 12px', 
                  borderRadius: '6px',
                  cursor: 'pointer'
                }}
              >
                <FaChevronLeft />
              </button>
              <h4 style={{ margin: 0, color: '#1f2937' }}>
                {selectedDate.toLocaleDateString(language === 'ar' ? 'ar-SA' : 'en-US', { month: 'long', year: 'numeric' })}
              </h4>
              <button 
                onClick={nextMonth}
                style={{ 
                  background: '#f3f4f6', 
                  border: 'none', 
                  padding: '8px 12px', 
                  borderRadius: '6px',
                  cursor: 'pointer'
                }}
              >
                <FaChevronRight />
              </button>
            </div>
            
            {/* Day Headers */}
            <div style={{ 
              display: 'grid', 
              gridTemplateColumns: 'repeat(7, 1fr)', 
              gap: '4px',
              marginBottom: '8px',
              textAlign: 'center',
              fontSize: '0.8rem',
              color: '#6b7280'
            }}>
              {(language === 'ar' 
                ? ['الأحد', 'الإثنين', 'الثلاثاء', 'الأربعاء', 'الخميس', 'الجمعة', 'السبت']
                : ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']
              ).map(day => (
                <div key={day} style={{ fontWeight: '600' }}>{day}</div>
              ))}
            </div>
            
            {/* Calendar Grid */}
            <div style={{ 
              display: 'grid', 
              gridTemplateColumns: 'repeat(7, 1fr)', 
              gap: '4px'
            }}>
              {getDaysInMonth(selectedDate).map((day, idx) => {
                const tasksOnDay = day ? getTasksForDate(day) : [];
                const closureOnDay = day ? getClosureTasksForDate(day) : [];
                const isToday = day && day.toDateString() === new Date().toDateString();
                const hasOverdue = tasksOnDay.some(t => t.is_overdue);
                
                return (
                  <div
                    key={idx}
                    onClick={() => {
                      if (day && (tasksOnDay.length > 0 || closureOnDay.length > 0)) {
                        setSelectedDate(day);
                        setViewMode('list');
                        setTimeout(() => {
                          const element = document.querySelector(`[data-task-date="${day.toISOString().split('T')[0]}"]`);
                          if (element) {
                            element.scrollIntoView({ behavior: 'smooth', block: 'center' });
                            element.style.background = '#fef3c7';
                            setTimeout(() => {
                              element.style.background = '';
                            }, 2000);
                          }
                        }, 100);
                      }
                    }}
                    style={{
                      minHeight: '50px',
                      padding: '4px',
                      background: !day ? 'transparent' : isToday ? '#fef3c7' : '#f9fafb',
                      borderRadius: '6px',
                      border: isToday ? '2px solid #d97706' : '1px solid #e5e7eb',
                      opacity: day ? 1 : 0.3,
                      cursor: day && (tasksOnDay.length > 0 || closureOnDay.length > 0) ? 'pointer' : 'default',
                      transition: 'all 0.2s ease'
                    }}
                    onMouseEnter={(e) => {
                      if (day && (tasksOnDay.length > 0 || closureOnDay.length > 0)) {
                        e.currentTarget.style.background = '#fef3c7';
                        e.currentTarget.style.transform = 'scale(1.05)';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (day) {
                        e.currentTarget.style.background = isToday ? '#fef3c7' : '#f9fafb';
                        e.currentTarget.style.transform = 'scale(1)';
                      }
                    }}
                  >
                    {day && (
                      <>
                        <div style={{ 
                          fontSize: '0.8rem', 
                          fontWeight: isToday ? '700' : '500',
                          color: isToday ? '#d97706' : '#374151',
                          marginBottom: '2px'
                        }}>
                          {day.getDate()}
                        </div>
                        {tasksOnDay.length > 0 && (
                          <div style={{
                            width: '18px',
                            height: '18px',
                            borderRadius: '50%',
                            background: hasOverdue ? '#ef4444' : '#d97706',
                            color: 'white',
                            fontSize: '0.65rem',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontWeight: '600'
                          }}>
                            {tasksOnDay.length}
                          </div>
                        )}
                        {closureOnDay.length > 0 && (
                          <div style={{
                            width: '18px',
                            height: '18px',
                            borderRadius: '50%',
                            background: '#dc2626',
                            color: 'white',
                            fontSize: '0.65rem',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontWeight: '600',
                            marginTop: '2px'
                          }}>
                            {closureOnDay.length}
                          </div>
                        )}
                      </>
                    )}
                  </div>
                );
              })}
            </div>
            
            {/* Legend */}
            <div style={{ 
              display: 'flex', 
              gap: '15px', 
              marginTop: '15px', 
              justifyContent: 'center',
              fontSize: '0.8rem'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                <div style={{ width: '12px', height: '12px', borderRadius: '50%', background: '#d97706' }}></div>
                <span>{language === 'ar' ? 'مهام إعلان' : 'Ad Tasks'}</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                <div style={{ width: '12px', height: '12px', borderRadius: '50%', background: '#dc2626' }}></div>
                <span>{language === 'ar' ? 'إغلاق إعلان' : 'Close Ad'}</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                <div style={{ width: '12px', height: '12px', borderRadius: '50%', background: '#ef4444' }}></div>
                <span>{language === 'ar' ? 'متأخر' : 'Overdue'}</span>
              </div>
            </div>
          </div>
        ) : (
          /* List View with Swipe & Progress */
          <div style={{ display: 'grid', gap: '12px' }}>
            {myTasks.map(task => {
              const isOverdue = task.is_overdue;
              const hoursLeft = task.hours_until_deadline || 0;
              const isApproaching = !isOverdue && hoursLeft <= 4;
              const taskProgress = getTaskProgress(task);
              const swipeOffset = getSwipeOffset(task.id);
              
              return (
                <div
                  key={task.id}
                  style={{
                    position: 'relative',
                    overflow: 'hidden',
                    borderRadius: '12px'
                  }}
                >
                  {/* Swipe Background */}
                  <div style={{
                    position: 'absolute',
                    top: 0,
                    right: 0,
                    bottom: 0,
                    width: '100px',
                    background: '#16a34a',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'white',
                    fontWeight: '600',
                    fontSize: '0.8rem'
                  }}>
                    <FaPlus style={{ marginLeft: '5px' }} />
                    {language === 'ar' ? 'إعلان' : 'Create'}
                  </div>
                  
                  {/* Main Card */}
                  <div
                    data-task-date={task.sla_deadline ? new Date(task.sla_deadline).toISOString().split('T')[0] : ''}
                    onTouchStart={(e) => handleTouchStart(task.id, e)}
                    onTouchMove={(e) => handleTouchMove(task.id, e)}
                    onTouchEnd={() => handleTouchEnd(task.id, task)}
                    style={{
                      padding: '15px',
                      background: 'white',
                      borderRadius: '12px',
                      border: isOverdue ? '2px solid #ef4444' : isApproaching ? '2px solid #f59e0b' : '1px solid #e5e7eb',
                      boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
                      transform: `translateX(${-swipeOffset}px)`,
                      transition: swipeStates[task.id]?.swiping ? 'none' : 'transform 0.3s ease'
                    }}
                  >
                    {/* Progress Bar - Clickable */}
                    <div 
                      onClick={() => !task.listing_created && openListingForm(task)}
                      style={{ 
                        marginBottom: '12px',
                        background: '#e5e7eb',
                        borderRadius: '10px',
                        height: '8px',
                        overflow: 'hidden',
                        cursor: !task.listing_created ? 'pointer' : 'default',
                        position: 'relative'
                      }}
                      title={!task.listing_created ? (language === 'ar' ? 'انقر لإنشاء الإعلان' : 'Click to create listing') : ''}
                    >
                      <div style={{
                        width: `${taskProgress}%`,
                        height: '100%',
                        background: getProgressColor(taskProgress),
                        borderRadius: '10px',
                        transition: 'width 0.5s ease'
                      }}></div>
                    </div>
                    
                    {/* Progress Labels */}
                    <div style={{ 
                      display: 'flex', 
                      justifyContent: 'space-between', 
                      fontSize: '0.7rem', 
                      color: '#6b7280',
                      marginBottom: '12px'
                    }}>
                      <span style={{ 
                        color: taskProgress >= 25 ? getProgressColor(25) : '#d1d5db',
                        fontWeight: taskProgress >= 25 ? '600' : '400'
                      }}>
                        {language === 'ar' ? '✓ معين' : '✓ Assigned'}
                      </span>
                      <span style={{ 
                        color: taskProgress >= 50 ? getProgressColor(50) : '#d1d5db',
                        fontWeight: taskProgress >= 50 ? '600' : '400'
                      }}>
                        {language === 'ar' ? '⏳ قيد العمل' : '⏳ In Progress'}
                      </span>
                      <span style={{ 
                        color: taskProgress >= 75 ? getProgressColor(75) : '#d1d5db',
                        fontWeight: taskProgress >= 75 ? '600' : '400'
                      }}>
                        {language === 'ar' ? '📢 منشور' : '📢 Posted'}
                      </span>
                      <span style={{ 
                        color: taskProgress >= 100 ? getProgressColor(100) : '#d1d5db',
                        fontWeight: taskProgress >= 100 ? '600' : '400'
                      }}>
                        {language === 'ar' ? '✅ مكتمل' : '✅ Done'}
                      </span>
                    </div>
                    
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                      <div style={{ flex: 1 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                        <FaBuilding style={{ color: '#6b7280' }} />
                        <strong style={{ fontSize: '1rem' }}>
                          {language === 'ar' ? 'وحدة' : 'Unit'} {task.unit_number}
                        </strong>
                        <span style={{
                          padding: '2px 8px',
                          background: '#f3f4f6',
                          borderRadius: '4px',
                          fontSize: '0.75rem'
                        }}>
                          {language === 'ar' 
                            ? (task.unit_type === 'apartment' ? 'شقة' : 
                               task.unit_type === 'villa' ? 'فيلا' : 
                               task.unit_type === 'office' ? 'مكتب' : 
                               task.unit_type === 'shop' ? 'محل' : 
                               task.unit_type === 'warehouse' ? 'مستودع' : 
                               task.unit_type || 'شقة')
                            : (task.unit_type || 'Apartment')}
                        </span>
                      </div>
                      
                      {task.property_name && (
                        <div style={{ fontSize: '0.85rem', color: '#6b7280', marginBottom: '4px' }}>
                          {task.property_name}
                        </div>
                      )}
                      
                      {task.monthly_rent && (
                        <div style={{ fontSize: '0.9rem', color: '#059669', fontWeight: '600' }}>
                          {task.monthly_rent.toLocaleString()} {language === 'ar' ? 'ر.س/شهر' : 'SAR/month'}
                        </div>
                      )}
                      
                      {task.listing_created && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            openViewListingModal(null, task.contact_id);
                          }}
                          style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '4px',
                            padding: '6px 12px',
                            background: '#f0fdf4',
                            border: '1px solid #10b981',
                            borderRadius: '6px',
                            color: '#059669',
                            cursor: 'pointer',
                            fontSize: '0.8rem',
                            fontWeight: '600',
                            marginTop: '8px'
                          }}
                          title={language === 'ar' ? 'عرض تفاصيل الإعلان' : 'View listing details'}
                        >
                          <FaExternalLinkAlt style={{ fontSize: '10px' }} />
                          {language === 'ar' ? 'عرض الإعلان' : 'View Ad'}
                        </button>
                      )}
                    </div>
                    
                    <div style={{ textAlign: 'left' }}>
                      {isOverdue ? (
                        <div style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '4px',
                          color: '#ef4444',
                          fontWeight: '600',
                          fontSize: '0.85rem'
                        }}>
                          <FaExclamationTriangle />
                          {language === 'ar' ? 'متأخر!' : 'Overdue!'}
                        </div>
                      ) : (
                        <div style={{
                          fontSize: '0.85rem',
                          color: isApproaching ? '#f59e0b' : '#6b7280'
                        }}>
                          <FaClock style={{ marginLeft: '4px' }} />
                          {Math.round(hoursLeft)} {language === 'ar' ? 'ساعة متبقية' : 'hours left'}
                        </div>
                      )}
                    </div>
                  </div>
                  
                  {/* Primary Action Button - Positioned at bottom right (LTR) or bottom left (RTL) */}
                  <div style={{ 
                    display: 'flex', 
                    justifyContent: language === 'ar' ? 'flex-start' : 'flex-end', 
                    marginTop: '16px',
                    paddingTop: '12px',
                    borderTop: '1px solid #e5e7eb'
                  }}>
                    <button
                      onClick={() => openListingForm(task)}
                      style={{
                        padding: '10px 20px',
                        background: 'linear-gradient(135deg, #059669 0%, #047857 100%)',
                        color: 'white',
                        border: 'none',
                        borderRadius: '10px',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        fontSize: '0.9rem',
                        fontWeight: '600',
                        boxShadow: '0 2px 8px rgba(5, 150, 105, 0.3)',
                        transition: 'all 0.2s ease',
                        whiteSpace: 'nowrap'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.transform = 'translateY(-2px)';
                        e.currentTarget.style.boxShadow = '0 4px 12px rgba(5, 150, 105, 0.4)';
                        e.currentTarget.style.background = 'linear-gradient(135deg, #047857 0%, #065f46 100%)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.transform = 'translateY(0)';
                        e.currentTarget.style.boxShadow = '0 2px 8px rgba(5, 150, 105, 0.3)';
                        e.currentTarget.style.background = 'linear-gradient(135deg, #059669 0%, #047857 100%)';
                      }}
                    >
                      <FaPlus />
                      {language === 'ar' ? 'إنشاء الإعلان' : 'Create Listing'}
                    </button>
                  </div>
                </div>
              </div>
              );
            })}
          </div>
        )}
      </div>
      )}

      {/* Ad Closure Tasks Section - REGA Compliance */}
      {(activeSection === 'tasks' || activeSection === 'overview') && isMarketing && closureTasks.length > 0 && (
      <div style={{
        marginBottom: '25px',
        padding: '20px',
        background: 'linear-gradient(135deg, #fef2f2 0%, #fff5f5 100%)',
        borderRadius: '16px',
        border: '2px solid #fca5a5'
      }}>
        <div style={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center', 
          marginBottom: '15px' 
        }}>
          <h3 style={{ 
            margin: 0, 
            display: 'flex', 
            alignItems: 'center', 
            gap: '10px',
            fontSize: '1.1rem',
            color: '#dc2626'
          }}>
            🔴 {language === 'ar' ? 'إعلانات تحتاج إغلاق (REGA)' : 'Ads Pending Closure (REGA)'}
            {loadingClosureTasks && (
              <span style={{ fontSize: '0.7rem', opacity: 0.6 }}>
                ({language === 'ar' ? 'جاري التحميل...' : 'loading...'})
              </span>
            )}
          </h3>
          <span style={{
            padding: '4px 12px',
            background: '#dc2626',
            color: 'white',
            borderRadius: '20px',
            fontSize: '0.85rem',
            fontWeight: '600'
          }}>
            {closureTasks.length} {language === 'ar' ? 'إعلان' : 'ads'}
          </span>
        </div>

        <p style={{ 
          margin: '0 0 15px 0', 
          padding: '10px', 
          background: '#fee2e2', 
          borderRadius: '8px',
          fontSize: '0.85rem',
          color: '#991b1b'
        }}>
          ⚠️ {language === 'ar' 
            ? 'تم استلام عربون لهذه الوحدات. يجب إزالة الإعلان من المنصة الخارجية خلال يومين وفقاً لأنظمة REGA.'
            : 'Deposit received for these units. Ads must be removed from external platform within 2 days per REGA regulations.'}
        </p>

        <div style={{ display: 'grid', gap: '12px' }}>
          {closureTasks.map(listing => {
            const isOverdue = listing.is_rega_overdue;
            const deadline = listing.rega_deadline ? new Date(listing.rega_deadline) : null;
            const hoursLeft = deadline ? Math.max(0, (deadline - new Date()) / (1000 * 60 * 60)) : null;
            
            return (
              <div
                key={listing.id}
                style={{
                  padding: '15px',
                  background: 'white',
                  borderRadius: '12px',
                  border: isOverdue ? '2px solid #dc2626' : '2px solid #fca5a5',
                  boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                      <FaBuilding style={{ color: '#dc2626' }} />
                      <strong style={{ fontSize: '1rem' }}>
                        {language === 'ar' ? 'وحدة' : 'Unit'} {listing.unit_number}
                      </strong>
                      {listing.platform && (
                        <span style={{
                          padding: '2px 8px',
                          background: '#f3f4f6',
                          borderRadius: '4px',
                          fontSize: '0.75rem'
                        }}>
                          {listing.platform}
                        </span>
                      )}
                    </div>
                    
                    {listing.property_name && (
                      <div style={{ fontSize: '0.85rem', color: '#6b7280', marginBottom: '4px' }}>
                        {listing.property_name}
                      </div>
                    )}
                    
                    {listing.external_url && (
                      <a 
                        href={listing.external_url} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        onClick={(e) => e.stopPropagation()}
                        style={{ fontSize: '0.85rem', color: '#2563eb', marginRight: '8px' }}
                      >
                        {language === 'ar' ? '🔗 رابط الإعلان' : '🔗 Ad Link'}
                      </a>
                    )}
                    
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        openViewListingModal(listing.id, listing.unit_id);
                      }}
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '4px',
                        padding: '4px 10px',
                        background: '#f0fdf4',
                        border: '1px solid #10b981',
                        borderRadius: '6px',
                        color: '#059669',
                        cursor: 'pointer',
                        fontSize: '0.8rem',
                        fontWeight: '600',
                        marginTop: '4px'
                      }}
                      title={language === 'ar' ? 'عرض تفاصيل الإعلان' : 'View listing details'}
                    >
                      <FaExternalLinkAlt style={{ fontSize: '10px' }} />
                      {language === 'ar' ? 'عرض التفاصيل' : 'View Details'}
                    </button>
                  </div>
                  
                  <div style={{ textAlign: 'left' }}>
                    {isOverdue ? (
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px',
                        color: '#dc2626',
                        fontWeight: '600',
                        fontSize: '0.85rem'
                      }}>
                        <FaExclamationTriangle />
                        {language === 'ar' ? 'تجاوز المهلة!' : 'Overdue!'}
                      </div>
                    ) : hoursLeft !== null ? (
                      <div style={{
                        fontSize: '0.85rem',
                        color: hoursLeft <= 12 ? '#dc2626' : '#d97706'
                      }}>
                        <FaClock style={{ marginLeft: '4px' }} />
                        {Math.round(hoursLeft)} {language === 'ar' ? 'ساعة متبقية' : 'hours left'}
                      </div>
                    ) : null}
                  </div>
                </div>
                
                {/* Primary Action Button - Positioned at bottom right (LTR) or bottom left (RTL) */}
                <div style={{ 
                  display: 'flex', 
                  justifyContent: language === 'ar' ? 'flex-start' : 'flex-end', 
                  marginTop: '16px',
                  paddingTop: '12px',
                  borderTop: '1px solid #e5e7eb'
                }}>
                  <button
                    onClick={() => handleCloseAd(listing.id)}
                    disabled={closingAd === listing.id}
                    style={{
                      padding: '10px 24px',
                      background: closingAd === listing.id 
                        ? '#9ca3af' 
                        : 'linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)',
                      color: 'white',
                      border: 'none',
                      borderRadius: '10px',
                      cursor: closingAd === listing.id ? 'not-allowed' : 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      fontSize: '0.9rem',
                      fontWeight: '600',
                      boxShadow: closingAd === listing.id 
                        ? 'none' 
                        : '0 2px 8px rgba(220, 38, 38, 0.3)',
                      transition: 'all 0.2s ease',
                      whiteSpace: 'nowrap'
                    }}
                    onMouseEnter={(e) => {
                      if (!closingAd) {
                        e.currentTarget.style.transform = 'translateY(-2px)';
                        e.currentTarget.style.boxShadow = '0 4px 12px rgba(220, 38, 38, 0.4)';
                        e.currentTarget.style.background = 'linear-gradient(135deg, #b91c1c 0%, #991b1b 100%)';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!closingAd) {
                        e.currentTarget.style.transform = 'translateY(0)';
                        e.currentTarget.style.boxShadow = '0 2px 8px rgba(220, 38, 38, 0.3)';
                        e.currentTarget.style.background = 'linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)';
                      }
                    }}
                  >
                    {closingAd === listing.id ? (
                      <><FaSpinner style={{ animation: 'spin 1s linear infinite' }} /> {language === 'ar' ? 'جاري التأكيد...' : 'Confirming...'}</>
                    ) : (
                      <><FaCheckCircle /> {language === 'ar' ? 'تأكيد إغلاق الإعلان' : 'Confirm Ad Closed'}</>
                    )}
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      </div>
      )}

      {/* Units with Listings Section (for deposit recording) */}
      {(activeSection === 'units' || activeSection === 'overview') && isMarketing && (
        <div className="card">
          <h2 style={{ marginBottom: '20px', display: 'flex', alignItems: 'center', gap: '10px' }}>
            <FaBuilding style={{ color: '#f97316' }} />
            {language === 'ar' ? 'الوحدات مع الإعلانات (تسجيل العربون)' : 'Units with Listings (Record Deposits)'}
          </h2>

          {loadingUnits ? (
            <div style={{ textAlign: 'center', padding: '40px' }}>
              <FaSpinner style={{ animation: 'spin 1s linear infinite', fontSize: '2rem', color: '#f97316' }} />
            </div>
          ) : unitsWithListings.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
              <FaBuilding style={{ fontSize: '3rem', color: '#ccc', marginBottom: '20px' }} />
              <p>{language === 'ar' ? 'لا توجد وحدات مع إعلانات نشطة' : 'No units with active listings'}</p>
            </div>
          ) : (
            <div style={{ display: 'grid', gap: '20px' }}>
              {unitsWithListings.map((unit) => (
                <div
                  key={unit.id}
                  style={{
                    border: '2px solid #e0e0e0',
                    borderRadius: '8px',
                    padding: '20px',
                    background: '#ffffff'
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '15px' }}>
                    <div>
                      <h3 style={{ margin: '0 0 10px 0', fontSize: '1.2rem', color: '#1f2937' }}>
                        {unit.property_name && `${unit.property_name} - `}{unit.unit_number}
                      </h3>
                      <p style={{ margin: '5px 0', color: '#666', fontSize: '0.9rem' }}>
                        {unit.unit_type} • {language === 'ar' ? 'إيجار شهري:' : 'Monthly Rent:'} {unit.monthly_rent || 'N/A'} {language === 'ar' ? 'ريال' : 'SAR'}
                      </p>
                      {unit.active_listing && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '8px' }}>
                          <p style={{ margin: 0, fontSize: '0.85rem', color: '#059669' }}>
                            ✓ {language === 'ar' ? 'إعلان نشط' : 'Active Listing'} • {unit.active_listing.status === 'ad_posted' ? (language === 'ar' ? 'تم النشر' : 'Posted') : (language === 'ar' ? 'قيد الانتظار' : 'Pending')}
                          </p>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              openViewListingModal(unit.active_listing.id, unit.id, unit.active_listing);
                            }}
                            style={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              padding: '4px 8px',
                              background: '#f0fdf4',
                              border: '1px solid #10b981',
                              borderRadius: '6px',
                              color: '#059669',
                              cursor: 'pointer',
                              fontSize: '0.75rem',
                              fontWeight: '600'
                            }}
                            title={language === 'ar' ? 'عرض تفاصيل الإعلان' : 'View listing details'}
                          >
                            <FaExternalLinkAlt style={{ fontSize: '10px' }} />
                          </button>
                        </div>
                      )}
                    </div>
                    <div>
                      <span
                        style={{
                          padding: '6px 12px',
                          borderRadius: '6px',
                          background: unit.status === 'reserved' ? '#dc2626' : unit.status === 'vacant' ? '#059669' : '#6b7280',
                          color: 'white',
                          fontSize: '0.85rem',
                          fontWeight: '500'
                        }}
                      >
                        {unit.status === 'reserved' ? (language === 'ar' ? 'محجوز' : 'Reserved') : 
                         unit.status === 'vacant' ? (language === 'ar' ? 'شاغر' : 'Vacant') : 
                         (language === 'ar' ? 'مشغول' : 'Occupied')}
                      </span>
                    </div>
                  </div>

                  {unit.active_deposit ? (
                    <div>
                      <div style={{ 
                        padding: '12px', 
                        backgroundColor: '#fef3c7', 
                        borderRadius: '6px', 
                        marginBottom: '15px',
                        border: '1px solid #fcd34d'
                      }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <div>
                            <p style={{ margin: '0 0 5px 0', fontWeight: '600', color: '#92400e' }}>
                              {language === 'ar' ? 'عربون مسجل:' : 'Deposit Recorded:'}
                            </p>
                            <p style={{ margin: '0', fontSize: '0.9rem', color: '#78350f' }}>
                              {unit.active_deposit.client_name} • {unit.active_deposit.amount} {language === 'ar' ? 'ريال' : 'SAR'}
                            </p>
                          </div>
                          <FaMoneyBillWave style={{ fontSize: '1.5rem', color: '#d97706' }} />
                        </div>
                      </div>
                      {/* Secondary Action Button - Positioned at bottom right (LTR) or bottom left (RTL) */}
                      <div style={{ 
                        display: 'flex', 
                        justifyContent: language === 'ar' ? 'flex-start' : 'flex-end', 
                        marginTop: '16px',
                        paddingTop: '12px',
                        borderTop: '1px solid #e5e7eb'
                      }}>
                        <button
                          onClick={() => openCancelDepositModal(unit.active_deposit, unit)}
                          style={{
                            padding: '10px 24px',
                            background: 'linear-gradient(135deg, #fee2e2 0%, #fecaca 100%)',
                            color: '#dc2626',
                            border: '2px solid #f87171',
                            borderRadius: '10px',
                            cursor: 'pointer',
                            fontSize: '0.9rem',
                            fontWeight: '600',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                            boxShadow: '0 2px 8px rgba(220, 38, 38, 0.2)',
                            transition: 'all 0.2s ease',
                            whiteSpace: 'nowrap'
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.transform = 'translateY(-2px)';
                            e.currentTarget.style.boxShadow = '0 4px 12px rgba(220, 38, 38, 0.3)';
                            e.currentTarget.style.borderColor = '#dc2626';
                            e.currentTarget.style.background = 'linear-gradient(135deg, #fecaca 0%, #fca5a5 100%)';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.transform = 'translateY(0)';
                            e.currentTarget.style.boxShadow = '0 2px 8px rgba(220, 38, 38, 0.2)';
                            e.currentTarget.style.borderColor = '#f87171';
                            e.currentTarget.style.background = 'linear-gradient(135deg, #fee2e2 0%, #fecaca 100%)';
                          }}
                        >
                          <FaExclamationTriangle />
                          {language === 'ar' ? 'طلب إلغاء العربون' : 'Request Deposit Cancellation'}
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div style={{
                      display: 'flex',
                      justifyContent: language === 'ar' ? 'flex-start' : 'flex-end', 
                      marginTop: '16px',
                      paddingTop: '12px',
                      borderTop: '1px solid #e5e7eb'
                    }}>
                      {/* Primary Action Button - Positioned at bottom right (LTR) or bottom left (RTL) */}
                      <button
                        onClick={() => openDepositModal(unit)}
                        style={{
                          padding: '10px 24px',
                          background: 'linear-gradient(135deg, #f97316 0%, #ea580c 100%)',
                          color: 'white',
                          border: 'none',
                          borderRadius: '10px',
                          cursor: 'pointer',
                          fontSize: '0.9rem',
                          fontWeight: '600',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px',
                          boxShadow: '0 2px 8px rgba(249, 115, 22, 0.3)',
                          transition: 'all 0.2s ease',
                          whiteSpace: 'nowrap'
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.transform = 'translateY(-2px)';
                          e.currentTarget.style.boxShadow = '0 4px 12px rgba(249, 115, 22, 0.4)';
                          e.currentTarget.style.background = 'linear-gradient(135deg, #ea580c 0%, #c2410c 100%)';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.transform = 'translateY(0)';
                          e.currentTarget.style.boxShadow = '0 2px 8px rgba(249, 115, 22, 0.3)';
                          e.currentTarget.style.background = 'linear-gradient(135deg, #f97316 0%, #ea580c 100%)';
                        }}
                      >
                        <FaMoneyBillWave />
                        {language === 'ar' ? 'تسجيل عربون' : 'Record Deposit'}
                      </button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Deals Section */}
      {(activeSection === 'deals' || activeSection === 'overview') && (
      <div className="card">
        <h2 style={{ marginBottom: '20px' }}>
          {language === 'ar' ? 'الصفقات المخصصة لي' : 'My Assigned Deals'}
        </h2>

        {deals.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
            <FaHandshake style={{ fontSize: '3rem', color: '#ccc', marginBottom: '20px' }} />
            <p>{language === 'ar' ? 'لا توجد صفقات مخصصة لك' : 'No deals assigned to you'}</p>
          </div>
        ) : (
          <div style={{ display: 'grid', gap: '20px' }}>
            {deals.map((deal) => (
              <div
                key={deal.id}
                className="employee-deal-card"
                style={{
                  border: '2px solid #e0e0e0',
                  borderRadius: '8px',
                  padding: '20px',
                  background: '#ffffff'
                }}
              >
                <div className="employee-deal-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '15px' }}>
                  <div>
                    <h3 style={{ margin: '0 0 10px 0', fontSize: '1.2rem' }}>
                      {deal.contact_name || (language === 'ar' ? 'عميل غير معروف' : 'Unknown Client')}
                    </h3>
                    <p style={{ margin: '5px 0', color: '#666', fontSize: '0.9rem' }}>
                      {deal.property_address || (language === 'ar' ? 'لا يوجد عنوان' : 'No address')}
                    </p>
                    {deal.price && (
                      <p style={{ margin: '5px 0', fontWeight: 'bold', color: '#f97316' }}>
                        {deal.price}
                      </p>
                    )}
                  </div>
                  <div style={{ textAlign: language === 'ar' ? 'left' : 'right' }}>
                    <span
                      style={{
                        padding: '6px 12px',
                        borderRadius: '6px',
                        background: getStatusColor(deal.status),
                        color: 'white',
                        fontSize: '0.85rem',
                        fontWeight: '500'
                      }}
                    >
                      {getStatusLabel(deal.status)}
                    </span>
                  </div>
                </div>

                {/* Progress Bar */}
                <div style={{ marginBottom: '15px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '5px' }}>
                    <span style={{ fontSize: '0.9rem', fontWeight: '500' }}>
                      {language === 'ar' ? 'التقدم' : 'Progress'}
                    </span>
                    <span style={{ fontSize: '0.9rem', fontWeight: 'bold', color: '#f97316' }}>
                      {deal.progress || 0}%
                    </span>
                  </div>
                  <div
                    style={{
                      width: '100%',
                      height: '20px',
                      background: '#e0e0e0',
                      borderRadius: '10px',
                      overflow: 'hidden'
                    }}
                  >
                    <div
                      style={{
                        width: `${deal.progress || 0}%`,
                        height: '100%',
                        background: '#f97316',
                        transition: 'width 0.3s'
                      }}
                    />
                  </div>
                </div>

                {deal.progress_notes && (
                  <div style={{ marginBottom: '15px', padding: '10px', background: '#f5f5f5', borderRadius: '6px' }}>
                    <p style={{ margin: 0, fontSize: '0.9rem', color: '#666' }}>{deal.progress_notes}</p>
                  </div>
                )}

                <button
                  className="employee-deal-update-btn"
                  onClick={() => handleEdit(deal)}
                  style={{
                    padding: '8px 16px',
                    background: '#f97316',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontSize: '0.9rem',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}
                >
                  <FaEdit />
                  {language === 'ar' ? 'تحديث التقدم' : 'Update Progress'}
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
      )}

      {/* Edit Modal */}
      {editingDeal && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0,0,0,0.5)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 1000
          }}
          onClick={() => setEditingDeal(null)}
        >
          <div
            className="card"
            style={{
              maxWidth: '500px',
              width: '90%',
              maxHeight: '90vh',
              overflow: 'auto'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h2 style={{ marginBottom: '20px' }}>
              {language === 'ar' ? 'تحديث تقدم الصفقة' : 'Update Deal Progress'}
            </h2>

            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
                {language === 'ar' ? 'التقدم (%)' : 'Progress (%)'}
              </label>
              <input
                type="range"
                min="0"
                max="100"
                value={progress}
                onChange={(e) => setProgress(e.target.value)}
                style={{ width: '100%' }}
              />
              <div style={{ textAlign: 'center', marginTop: '5px', fontSize: '1.2rem', fontWeight: 'bold', color: '#f97316' }}>
                {progress}%
              </div>
            </div>

            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
                {language === 'ar' ? 'الحالة' : 'Status'}
              </label>
              <select
                value={status}
                onChange={(e) => setStatus(e.target.value)}
                style={{
                  width: '100%',
                  padding: '10px',
                  border: '2px solid #e0e0e0',
                  borderRadius: '6px',
                  fontSize: '1rem'
                }}
              >
                <option value="pending">{language === 'ar' ? 'قيد الانتظار' : 'Pending'}</option>
                <option value="in_progress">{language === 'ar' ? 'قيد التنفيذ' : 'In Progress'}</option>
                <option value="on_hold">{language === 'ar' ? 'معلق' : 'On Hold'}</option>
                <option value="completed">{language === 'ar' ? 'مكتمل' : 'Completed'}</option>
              </select>
            </div>

            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
                {language === 'ar' ? 'ملاحظات' : 'Notes'}
              </label>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows="4"
                style={{
                  width: '100%',
                  padding: '10px',
                  border: '2px solid #e0e0e0',
                  borderRadius: '6px',
                  fontSize: '1rem',
                  resize: 'vertical'
                }}
                placeholder={language === 'ar' ? 'أضف ملاحظات حول التقدم...' : 'Add notes about progress...'}
              />
            </div>

            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
              <button
                onClick={() => setEditingDeal(null)}
                style={{
                  padding: '10px 20px',
                  background: '#e0e0e0',
                  color: '#000',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer'
                }}
              >
                {language === 'ar' ? 'إلغاء' : 'Cancel'}
              </button>
              <button
                onClick={handleSave}
                style={{
                  padding: '10px 20px',
                  background: '#f97316',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}
              >
                <FaCheckCircle />
                {language === 'ar' ? 'حفظ' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Listing Creation Form Modal */}
      {showListingForm && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0,0,0,0.5)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 1000
          }}
          onClick={() => setShowListingForm(null)}
        >
          <div
            className="card"
            style={{
              maxWidth: '600px',
              width: '90%',
              maxHeight: '90vh',
              overflow: 'auto',
              background: 'white',
              borderRadius: '20px',
              padding: '30px',
              boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
              animation: 'slideIn 0.3s ease-out'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px', paddingBottom: '20px', borderBottom: '2px solid #f3f4f6' }}>
              <h2 style={{ margin: 0, display: 'flex', alignItems: 'center', gap: '12px', fontSize: '1.2rem', fontWeight: '600', color: '#1f2937' }}>
                <div style={{
                  width: '40px',
                  height: '40px',
                  borderRadius: '10px',
                  background: 'linear-gradient(135deg, #059669 0%, #047857 100%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'white',
                  boxShadow: '0 4px 12px rgba(5, 150, 105, 0.3)'
                }}>
                  <FaPlus style={{ fontSize: '18px' }} />
                </div>
                <span>
                  {language === 'ar' ? 'إنشاء إعلان للوحدة' : 'Create Listing for Unit'}{' '}
                  <span style={{ color: '#059669', fontWeight: '600' }}>{showListingForm.unit_number}</span>
                </span>
              </h2>
              <button
                onClick={() => setShowListingForm(null)}
                style={{
                  background: '#f3f4f6',
                  border: 'none',
                  borderRadius: '10px',
                  width: '36px',
                  height: '36px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  color: '#6b7280',
                  transition: 'all 0.2s'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = '#e5e7eb';
                  e.currentTarget.style.color = '#374151';
                  e.currentTarget.style.transform = 'rotate(90deg)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = '#f3f4f6';
                  e.currentTarget.style.color = '#6b7280';
                  e.currentTarget.style.transform = 'rotate(0deg)';
                }}
              >
                <FaTimes />
              </button>
            </div>

            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px', fontWeight: '600', color: '#374151', fontSize: '0.95rem' }}>
                <FaFileAlt style={{ fontSize: '16px', color: '#059669' }} />
                {language === 'ar' ? 'عنوان الإعلان' : 'Listing Title'} <span style={{ color: '#dc2626' }}>*</span>
              </label>
              <input
                type="text"
                value={listingFormData.title}
                onChange={(e) => setListingFormData({...listingFormData, title: e.target.value})}
                required
                style={{
                  width: '100%',
                  padding: '14px',
                  border: '2px solid #e5e7eb',
                  borderRadius: '12px',
                  fontSize: '1rem',
                  transition: 'all 0.2s',
                  boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
                }}
                onFocus={(e) => {
                  e.currentTarget.style.borderColor = '#059669';
                  e.currentTarget.style.boxShadow = '0 0 0 3px rgba(5, 150, 105, 0.1)';
                }}
                onBlur={(e) => {
                  e.currentTarget.style.borderColor = '#e5e7eb';
                  e.currentTarget.style.boxShadow = '0 2px 4px rgba(0,0,0,0.05)';
                }}
                placeholder={language === 'ar' ? 'مثال: شقة فاخرة للإيجار' : 'e.g. Luxury apartment for rent'}
              />
            </div>

            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px', fontWeight: '600', color: '#374151', fontSize: '0.95rem' }}>
                <FaFileAlt style={{ fontSize: '16px', color: '#6b7280' }} />
                {language === 'ar' ? 'وصف الإعلان' : 'Description'}
              </label>
              <textarea
                value={listingFormData.description}
                onChange={(e) => setListingFormData({...listingFormData, description: e.target.value})}
                rows="4"
                style={{
                  width: '100%',
                  padding: '14px',
                  border: '2px solid #e5e7eb',
                  borderRadius: '12px',
                  fontSize: '1rem',
                  resize: 'vertical',
                  transition: 'all 0.2s',
                  boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
                  fontFamily: 'inherit'
                }}
                onFocus={(e) => {
                  e.currentTarget.style.borderColor = '#059669';
                  e.currentTarget.style.boxShadow = '0 0 0 3px rgba(5, 150, 105, 0.1)';
                }}
                onBlur={(e) => {
                  e.currentTarget.style.borderColor = '#e5e7eb';
                  e.currentTarget.style.boxShadow = '0 2px 4px rgba(0,0,0,0.05)';
                }}
                placeholder={language === 'ar' ? 'تفاصيل إضافية عن الوحدة...' : 'Additional details about the unit...'}
              />
            </div>

            {/* Operation Type */}
            <div style={{ marginBottom: '15px' }}>
              <label style={{ display: 'block', marginBottom: '10px', fontWeight: '500' }}>
                {language === 'ar' ? 'نوع العملية *' : 'Operation Type *'}
              </label>
              <div style={{ display: 'flex', gap: '15px' }}>
                <button
                  type="button"
                  onClick={() => setListingFormData({...listingFormData, operation_type: 'rent'})}
                  style={{
                    flex: 1,
                    padding: '15px',
                    border: listingFormData.operation_type === 'rent' ? '3px solid #059669' : '2px solid #e5e7eb',
                    borderRadius: '12px',
                    background: listingFormData.operation_type === 'rent' ? '#d1fae5' : 'white',
                    cursor: 'pointer',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: '8px',
                    fontWeight: '600',
                    color: listingFormData.operation_type === 'rent' ? '#059669' : '#6b7280',
                    transition: 'all 0.2s'
                  }}
                >
                  <FaHome style={{ fontSize: '28px' }} />
                  <span>{language === 'ar' ? 'تأجير' : 'Rent'}</span>
                </button>
                <button
                  type="button"
                  onClick={() => setListingFormData({...listingFormData, operation_type: 'sale'})}
                  style={{
                    flex: 1,
                    padding: '15px',
                    border: listingFormData.operation_type === 'sale' ? '3px solid #f97316' : '2px solid #e5e7eb',
                    borderRadius: '12px',
                    background: listingFormData.operation_type === 'sale' ? '#fed7aa' : 'white',
                    cursor: 'pointer',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: '8px',
                    fontWeight: '600',
                    color: listingFormData.operation_type === 'sale' ? '#f97316' : '#6b7280',
                    transition: 'all 0.2s'
                  }}
                >
                  <FaDollarSign style={{ fontSize: '28px' }} />
                  <span>{language === 'ar' ? 'بيع' : 'Sale'}</span>
                </button>
              </div>
            </div>

            {/* Client Search */}
            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: '#374151', fontSize: '0.95rem' }}>
                <FaPhone style={{ marginLeft: '6px', fontSize: '14px', color: '#6b7280' }} />
                {language === 'ar' ? 'بحث عن العميل (اختياري)' : 'Search Client (Optional)'}
              </label>
              <div style={{ position: 'relative' }}>
                <div style={{ display: 'flex', gap: '8px', alignItems: 'stretch' }}>
                  <div style={{ position: 'relative', flex: 1 }}>
                    <input
                      type="tel"
                      value={listingFormData.client_phone}
                      onChange={(e) => {
                        const phone = e.target.value;
                        setListingFormData({...listingFormData, client_phone: phone, client_name: ''});
                        if (phone.length >= 5) {
                          searchClientByPhone(phone);
                        } else {
                          setClientSearchResults([]);
                        }
                      }}
                      style={{
                        width: '100%',
                        padding: '12px 40px 12px 12px',
                        border: '2px solid #e5e7eb',
                        borderRadius: '10px',
                        fontSize: '1rem',
                        transition: 'all 0.2s',
                        boxShadow: '0 1px 3px rgba(0,0,0,0.05)'
                      }}
                      onFocus={(e) => {
                        e.currentTarget.style.borderColor = '#059669';
                        e.currentTarget.style.boxShadow = '0 0 0 3px rgba(5, 150, 105, 0.1)';
                      }}
                      onBlur={(e) => {
                        e.currentTarget.style.borderColor = '#e5e7eb';
                        e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.05)';
                      }}
                      placeholder={language === 'ar' ? '05xxxxxxxx' : '05xxxxxxxx'}
                    />
                    {searchingClient ? (
                      <FaSpinner style={{ 
                        position: 'absolute', 
                        right: '14px', 
                        top: '50%', 
                        transform: 'translateY(-50%)',
                        color: '#059669',
                        animation: 'spin 1s linear infinite'
                      }} />
                    ) : (
                      <FaSearch style={{ 
                        position: 'absolute', 
                        right: '14px', 
                        top: '50%', 
                        transform: 'translateY(-50%)',
                        color: '#9ca3af'
                      }} />
                    )}
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      setNewClientData({ name: '', phone: listingFormData.client_phone || '', email: '' });
                      setShowNewClientModal(true);
                    }}
                    style={{
                      padding: '12px 16px',
                      background: 'linear-gradient(135deg, #059669 0%, #047857 100%)',
                      color: 'white',
                      border: 'none',
                      borderRadius: '10px',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px',
                      fontWeight: '600',
                      fontSize: '0.9rem',
                      boxShadow: '0 2px 8px rgba(5, 150, 105, 0.3)',
                      transition: 'all 0.2s'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = 'translateY(-2px)';
                      e.currentTarget.style.boxShadow = '0 4px 12px rgba(5, 150, 105, 0.4)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.boxShadow = '0 2px 8px rgba(5, 150, 105, 0.3)';
                    }}
                  >
                    <FaUserPlus />
                    {language === 'ar' ? 'جديد' : 'New'}
                  </button>
                </div>
                {clientSearchResults.length > 0 && (
                  <div style={{
                    position: 'absolute',
                    top: '100%',
                    left: 0,
                    right: 0,
                    background: 'white',
                    border: '2px solid #e5e7eb',
                    borderRadius: '12px',
                    marginTop: '8px',
                    maxHeight: '220px',
                    overflowY: 'auto',
                    zIndex: 1001,
                    boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
                    animation: 'fadeIn 0.2s ease-out'
                  }}>
                    {clientSearchResults.map(client => (
                      <div
                        key={client.id}
                        onClick={() => {
                          setListingFormData({
                            ...listingFormData,
                            client_phone: client.phone,
                            client_name: client.name
                          });
                          setClientSearchResults([]);
                        }}
                        style={{
                          padding: '14px 16px',
                          cursor: 'pointer',
                          borderBottom: '1px solid #f3f4f6',
                          transition: 'all 0.15s',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '12px'
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.background = '#f0fdf4';
                          e.currentTarget.style.paddingLeft = '20px';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.background = 'white';
                          e.currentTarget.style.paddingLeft = '16px';
                        }}
                      >
                        <div style={{
                          width: '40px',
                          height: '40px',
                          borderRadius: '50%',
                          background: 'linear-gradient(135deg, #059669 0%, #047857 100%)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: 'white',
                          fontWeight: '700',
                          fontSize: '0.9rem'
                        }}>
                          {client.name ? client.name.charAt(0).toUpperCase() : '?'}
                        </div>
                        <div style={{ flex: 1 }}>
                          <div style={{ fontWeight: '600', color: '#1f2937', marginBottom: '2px' }}>{client.name}</div>
                          <div style={{ fontSize: '0.85rem', color: '#6b7280', display: 'flex', alignItems: 'center', gap: '6px' }}>
                            <FaPhone style={{ fontSize: '10px' }} />
                            {client.phone}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              {listingFormData.client_name && (
                <div style={{ 
                  marginTop: '10px', 
                  padding: '12px', 
                  background: 'linear-gradient(135deg, #d1fae5 0%, #a7f3d0 100%)', 
                  borderRadius: '10px', 
                  fontSize: '0.9rem', 
                  color: '#065f46',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  fontWeight: '600',
                  border: '2px solid #10b981',
                  animation: 'fadeIn 0.3s ease-out'
                }}>
                  <FaCheckCircle style={{ color: '#059669' }} />
                  {language === 'ar' ? 'العميل المحدد:' : 'Selected Client:'} {listingFormData.client_name}
                </div>
              )}
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px', marginBottom: '20px' }}>
              <div>
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px', fontWeight: '600', color: '#374151', fontSize: '0.95rem' }}>
                  <FaLink style={{ fontSize: '16px', color: '#6b7280' }} />
                  {language === 'ar' ? 'المنصة' : 'Platform'}
                </label>
                <select
                  value={listingFormData.platform}
                  onChange={(e) => setListingFormData({...listingFormData, platform: e.target.value, platform_other: ''})}
                  style={{
                    width: '100%',
                    padding: '14px',
                    border: '2px solid #e5e7eb',
                    borderRadius: '12px',
                    fontSize: '1rem',
                    background: 'white',
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
                  }}
                  onFocus={(e) => {
                    e.currentTarget.style.borderColor = '#059669';
                    e.currentTarget.style.boxShadow = '0 0 0 3px rgba(5, 150, 105, 0.1)';
                  }}
                  onBlur={(e) => {
                    e.currentTarget.style.borderColor = '#e5e7eb';
                    e.currentTarget.style.boxShadow = '0 2px 4px rgba(0,0,0,0.05)';
                  }}
                >
                  <option value="بيوت">بيوت</option>
                  <option value="ديل">ديل</option>
                  <option value="عقار">عقار</option>
                  <option value="وصلت">وصلت</option>
                  <option value="جاذر">جاذر</option>
                  <option value="أخرى">أخرى</option>
                </select>
              </div>
              
              <div>
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px', fontWeight: '600', color: '#374151', fontSize: '0.95rem' }}>
                  <FaDollarSign style={{ fontSize: '16px', color: '#059669' }} />
                  {language === 'ar' ? 'السعر (ر.س)' : 'Price (SAR)'}
                </label>
                <input
                  type="number"
                  value={listingFormData.price}
                  onChange={(e) => setListingFormData({...listingFormData, price: e.target.value})}
                  style={{
                    width: '100%',
                    padding: '14px',
                    border: '2px solid #e5e7eb',
                    borderRadius: '12px',
                    fontSize: '1rem',
                    transition: 'all 0.2s',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
                  }}
                  onFocus={(e) => {
                    e.currentTarget.style.borderColor = '#059669';
                    e.currentTarget.style.boxShadow = '0 0 0 3px rgba(5, 150, 105, 0.1)';
                  }}
                  onBlur={(e) => {
                    e.currentTarget.style.borderColor = '#e5e7eb';
                    e.currentTarget.style.boxShadow = '0 2px 4px rgba(0,0,0,0.05)';
                  }}
                  placeholder={language === 'ar' ? '0' : '0'}
                />
              </div>
            </div>

            {listingFormData.platform === 'أخرى' && (
              <div style={{ marginBottom: '20px', animation: 'fadeIn 0.3s ease-out' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px', fontWeight: '600', color: '#374151', fontSize: '0.95rem' }}>
                  <FaLink style={{ fontSize: '16px', color: '#f97316' }} />
                  {language === 'ar' ? 'اسم المنصة الأخرى' : 'Other Platform Name'}
                </label>
                <input
                  type="text"
                  value={listingFormData.platform_other}
                  onChange={(e) => setListingFormData({...listingFormData, platform_other: e.target.value})}
                  style={{
                    width: '100%',
                    padding: '14px',
                    border: '2px solid #fcd34d',
                    borderRadius: '12px',
                    fontSize: '1rem',
                    background: '#fffbeb',
                    transition: 'all 0.2s',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
                  }}
                  onFocus={(e) => {
                    e.currentTarget.style.borderColor = '#f97316';
                    e.currentTarget.style.boxShadow = '0 0 0 3px rgba(249, 115, 22, 0.1)';
                  }}
                  onBlur={(e) => {
                    e.currentTarget.style.borderColor = '#fcd34d';
                    e.currentTarget.style.boxShadow = '0 2px 4px rgba(0,0,0,0.05)';
                  }}
                  placeholder={language === 'ar' ? 'أدخل اسم المنصة' : 'Enter platform name'}
                />
              </div>
            )}

            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px', fontWeight: '600', color: '#374151', fontSize: '0.95rem' }}>
                <FaLink style={{ fontSize: '16px', color: '#6b7280' }} />
                {language === 'ar' ? 'رابط الإعلان على المنصة' : 'Platform ad URL'}
              </label>
              <input
                type="url"
                value={listingFormData.external_url}
                onChange={(e) => setListingFormData({...listingFormData, external_url: e.target.value})}
                style={{
                  width: '100%',
                  padding: '14px',
                  border: '2px solid #e5e7eb',
                  borderRadius: '12px',
                  fontSize: '1rem',
                  transition: 'all 0.2s',
                  boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
                }}
                onFocus={(e) => {
                  e.currentTarget.style.borderColor = '#059669';
                  e.currentTarget.style.boxShadow = '0 0 0 3px rgba(5, 150, 105, 0.1)';
                }}
                onBlur={(e) => {
                  e.currentTarget.style.borderColor = '#e5e7eb';
                  e.currentTarget.style.boxShadow = '0 2px 4px rgba(0,0,0,0.05)';
                }}
                placeholder="https://..."
              />
            </div>

            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px', fontWeight: '600', color: '#374151', fontSize: '0.95rem' }}>
                <FaFileAlt style={{ fontSize: '16px', color: '#6b7280' }} />
                {language === 'ar' ? 'رقم الإعلان على المنصة' : 'Platform ad number'}
              </label>
              <input
                type="text"
                value={listingFormData.platform_ad_number || ''}
                onChange={(e) => setListingFormData({...listingFormData, platform_ad_number: e.target.value})}
                style={{
                  width: '100%',
                  padding: '14px',
                  border: '2px solid #e5e7eb',
                  borderRadius: '12px',
                  fontSize: '1rem',
                  transition: 'all 0.2s',
                  boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
                }}
                placeholder={language === 'ar' ? 'مثال: 6743016 (عقار) أو 534449 (ديل)' : 'e.g. 6743016 (Aqar) or 534449 (Deal)'}
              />
              <p style={{ margin: '8px 0 0', fontSize: '0.78rem', color: '#6b7280', lineHeight: 1.4 }}>
                {language === 'ar'
                  ? 'رقم المنصة فقط — يختلف بين المنصات لنفس الوحدة. ليس رقم ترخيص الهيئة.'
                  : 'Platform ID only — differs per site for the same unit. Not the REGA license.'}
              </p>
            </div>

            {/* REGA Compliance Section */}
            <div style={{
              marginBottom: '20px',
              padding: '20px',
              background: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)',
              borderRadius: '16px',
              border: '2px solid #fcd34d',
              boxShadow: '0 4px 12px rgba(252, 211, 77, 0.2)'
            }}>
              <h4 style={{ 
                margin: '0 0 18px 0', 
                display: 'flex', 
                alignItems: 'center', 
                gap: '10px',
                color: '#92400e',
                fontSize: '1.1rem',
                fontWeight: '700'
              }}>
                <FaStore style={{ fontSize: '20px' }} />
                {language === 'ar' ? 'متطلبات الهيئة العامة للعقار (REGA)' : 'REGA Compliance'}
              </h4>
              
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px', marginBottom: '15px' }}>
                <div>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '8px', fontWeight: '600', fontSize: '0.9rem', color: '#78350f' }}>
                    <FaFileAlt style={{ fontSize: '14px' }} />
                    {language === 'ar' ? 'السجل التجاري (اختياري)' : 'Commercial Registry (Optional)'}
                  </label>
                  <input
                    type="text"
                    value={listingFormData.commercial_registry}
                    onChange={(e) => {
                      setListingFormData({...listingFormData, commercial_registry: e.target.value});
                      localStorage.setItem('commercial_registry', e.target.value);
                    }}
                    style={{
                      width: '100%',
                      padding: '12px',
                      border: '2px solid #fcd34d',
                      borderRadius: '10px',
                      fontSize: '1rem',
                      background: 'white',
                      transition: 'all 0.2s',
                      boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
                    }}
                    onFocus={(e) => {
                      e.currentTarget.style.borderColor = '#f59e0b';
                      e.currentTarget.style.boxShadow = '0 0 0 3px rgba(245, 158, 11, 0.1)';
                    }}
                    onBlur={(e) => {
                      e.currentTarget.style.borderColor = '#fcd34d';
                      e.currentTarget.style.boxShadow = '0 2px 4px rgba(0,0,0,0.05)';
                    }}
                    placeholder={language === 'ar' ? 'مثال: 1234567890' : 'e.g. 1234567890'}
                  />
                </div>
                
                <div>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '8px', fontWeight: '600', fontSize: '0.9rem', color: '#78350f' }}>
                    <FaFileAlt style={{ fontSize: '14px' }} />
                    {language === 'ar' ? 'رقم ترخيص الإعلان (هيئة العقار) *' : 'REGA ad license # *'}
                  </label>
                  <input
                    type="text"
                    value={listingFormData.ad_license}
                    onChange={(e) => setListingFormData({...listingFormData, ad_license: e.target.value})}
                    required
                    style={{
                      width: '100%',
                      padding: '12px',
                      border: '2px solid #fcd34d',
                      borderRadius: '10px',
                      fontSize: '1rem',
                      background: 'white',
                      transition: 'all 0.2s',
                      boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
                    }}
                    onFocus={(e) => {
                      e.currentTarget.style.borderColor = '#f59e0b';
                      e.currentTarget.style.boxShadow = '0 0 0 3px rgba(245, 158, 11, 0.1)';
                    }}
                    onBlur={(e) => {
                      e.currentTarget.style.borderColor = '#fcd34d';
                      e.currentTarget.style.boxShadow = '0 2px 4px rgba(0,0,0,0.05)';
                    }}
                    placeholder={language === 'ar' ? 'مثال: 7201009770' : 'e.g. 7201009770'}
                  />
                  <p style={{ margin: '6px 0 0', fontSize: '0.75rem', color: '#92400e', lineHeight: 1.35 }}>
                    {language === 'ar'
                      ? 'ترخيص الهيئة — يمكن استخدامه على عدة منصات لنفس الوحدة.'
                      : 'REGA license — reusable across platforms for the same unit.'}
                  </p>
                </div>
              </div>
              
              <label style={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: '10px',
                cursor: 'pointer',
                padding: '10px',
                background: listingFormData.has_brokerage_contract ? '#dcfce7' : 'white',
                borderRadius: '8px',
                border: listingFormData.has_brokerage_contract ? '2px solid #16a34a' : '2px solid #e5e7eb'
              }}>
                <input
                  type="checkbox"
                  checked={listingFormData.has_brokerage_contract}
                  onChange={(e) => setListingFormData({...listingFormData, has_brokerage_contract: e.target.checked})}
                  style={{ width: '20px', height: '20px' }}
                />
                <span style={{ fontWeight: '500' }}>
                  {language === 'ar' 
                    ? '✅ أؤكد وجود عقد وساطة ساري مع المالك' 
                    : '✅ I confirm valid brokerage contract with owner'}
                </span>
              </label>
              
              <p style={{ 
                margin: '10px 0 0 0', 
                fontSize: '0.8rem', 
                color: '#92400e',
                lineHeight: '1.4'
              }}>
                {language === 'ar' 
                  ? '⚠️ يجب إزالة الإعلان خلال يومين من تأجير الوحدة وفقاً لأنظمة الهيئة العامة للعقار'
                  : '⚠️ Ad must be removed within 2 days of rental per REGA regulations'}
              </p>
            </div>

            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end', marginTop: '24px' }}>
              <button
                onClick={() => setShowListingForm(null)}
                style={{
                  padding: '12px 24px',
                  background: '#f3f4f6',
                  color: '#374151',
                  border: 'none',
                  borderRadius: '12px',
                  cursor: 'pointer',
                  fontWeight: '600',
                  fontSize: '0.95rem',
                  transition: 'all 0.2s',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = '#e5e7eb';
                  e.currentTarget.style.transform = 'translateY(-2px)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = '#f3f4f6';
                  e.currentTarget.style.transform = 'translateY(0)';
                }}
              >
                <FaTimes />
                {language === 'ar' ? 'إلغاء' : 'Cancel'}
              </button>
              <button
                onClick={handleCreateListing}
                disabled={submittingListing || !listingFormData.title || !listingFormData.ad_license || !listingFormData.has_brokerage_contract}
                style={{
                  padding: '12px 28px',
                  background: submittingListing || !listingFormData.title || !listingFormData.ad_license || !listingFormData.has_brokerage_contract
                    ? '#9ca3af' 
                    : 'linear-gradient(135deg, #059669 0%, #047857 100%)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '12px',
                  cursor: submittingListing || !listingFormData.title || !listingFormData.ad_license || !listingFormData.has_brokerage_contract 
                    ? 'not-allowed' 
                    : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                  fontWeight: '600',
                  fontSize: '1rem',
                  boxShadow: submittingListing || !listingFormData.title || !listingFormData.ad_license || !listingFormData.has_brokerage_contract
                    ? 'none'
                    : '0 4px 12px rgba(5, 150, 105, 0.3)',
                  transition: 'all 0.2s'
                }}
                onMouseEnter={(e) => {
                  if (!submittingListing && listingFormData.title && listingFormData.ad_license && listingFormData.has_brokerage_contract) {
                    e.currentTarget.style.transform = 'translateY(-2px)';
                    e.currentTarget.style.boxShadow = '0 6px 16px rgba(5, 150, 105, 0.4)';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!submittingListing && listingFormData.title && listingFormData.ad_license && listingFormData.has_brokerage_contract) {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = '0 4px 12px rgba(5, 150, 105, 0.3)';
                  }
                }}
              >
                {submittingListing ? (
                  <>
                    <FaSpinner style={{ animation: 'spin 1s linear infinite' }} /> 
                    {language === 'ar' ? 'جاري الحفظ...' : 'Saving...'}
                  </>
                ) : (
                  <>
                    <FaCheckCircle /> 
                    {language === 'ar' ? 'إنشاء الإعلان' : 'Create Listing'}
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* New Client Modal */}
      {showNewClientModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 1001
        }}
        onClick={() => setShowNewClientModal(false)}
        >
          <div style={{
            backgroundColor: 'white',
            borderRadius: '20px',
            padding: '30px',
            width: '90%',
            maxWidth: '450px',
            boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
            animation: 'slideIn 0.3s ease-out'
          }}
          onClick={(e) => e.stopPropagation()}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px', paddingBottom: '20px', borderBottom: '2px solid #f3f4f6' }}>
              <h3 style={{ margin: 0, display: 'flex', alignItems: 'center', gap: '12px', fontSize: '1.3rem', fontWeight: '700', color: '#1f2937' }}>
                <div style={{
                  width: '40px',
                  height: '40px',
                  borderRadius: '10px',
                  background: 'linear-gradient(135deg, #059669 0%, #047857 100%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'white'
                }}>
                  <FaUserPlus />
                </div>
                {language === 'ar' ? 'إضافة عميل جديد' : 'Add New Client'}
              </h3>
              <button
                onClick={() => setShowNewClientModal(false)}
                style={{
                  background: '#f3f4f6',
                  border: 'none',
                  borderRadius: '10px',
                  width: '36px',
                  height: '36px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  color: '#6b7280',
                  transition: 'all 0.2s'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = '#e5e7eb';
                  e.currentTarget.style.transform = 'rotate(90deg)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = '#f3f4f6';
                  e.currentTarget.style.transform = 'rotate(0deg)';
                }}
              >
                <FaTimes />
              </button>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '18px' }}>
              <div>
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px', fontWeight: '600', color: '#374151', fontSize: '0.95rem' }}>
                  <FaUserPlus style={{ fontSize: '16px', color: '#059669' }} />
                  {language === 'ar' ? 'الاسم *' : 'Name *'}
                </label>
                <input
                  type="text"
                  value={newClientData.name}
                  onChange={(e) => setNewClientData({...newClientData, name: e.target.value})}
                  required
                  style={{
                    width: '100%',
                    padding: '14px',
                    border: '2px solid #e5e7eb',
                    borderRadius: '12px',
                    fontSize: '1rem',
                    transition: 'all 0.2s',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
                  }}
                  onFocus={(e) => {
                    e.currentTarget.style.borderColor = '#059669';
                    e.currentTarget.style.boxShadow = '0 0 0 3px rgba(5, 150, 105, 0.1)';
                  }}
                  onBlur={(e) => {
                    e.currentTarget.style.borderColor = '#e5e7eb';
                    e.currentTarget.style.boxShadow = '0 2px 4px rgba(0,0,0,0.05)';
                  }}
                  placeholder={language === 'ar' ? 'اسم العميل' : 'Client name'}
                />
              </div>

              <div>
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px', fontWeight: '600', color: '#374151', fontSize: '0.95rem' }}>
                  <FaPhone style={{ fontSize: '16px', color: '#059669' }} />
                  {language === 'ar' ? 'رقم الهاتف *' : 'Phone *'}
                </label>
                <input
                  type="tel"
                  value={newClientData.phone}
                  onChange={(e) => setNewClientData({...newClientData, phone: e.target.value})}
                  required
                  style={{
                    width: '100%',
                    padding: '14px',
                    border: '2px solid #e5e7eb',
                    borderRadius: '12px',
                    fontSize: '1rem',
                    transition: 'all 0.2s',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
                  }}
                  onFocus={(e) => {
                    e.currentTarget.style.borderColor = '#059669';
                    e.currentTarget.style.boxShadow = '0 0 0 3px rgba(5, 150, 105, 0.1)';
                  }}
                  onBlur={(e) => {
                    e.currentTarget.style.borderColor = '#e5e7eb';
                    e.currentTarget.style.boxShadow = '0 2px 4px rgba(0,0,0,0.05)';
                  }}
                  placeholder="05xxxxxxxx"
                />
              </div>

              <div>
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px', fontWeight: '600', color: '#374151', fontSize: '0.95rem' }}>
                  <FaEnvelope style={{ fontSize: '16px', color: '#6b7280' }} />
                  {language === 'ar' ? 'البريد الإلكتروني (اختياري)' : 'Email (Optional)'}
                </label>
                <input
                  type="email"
                  value={newClientData.email}
                  onChange={(e) => setNewClientData({...newClientData, email: e.target.value})}
                  style={{
                    width: '100%',
                    padding: '14px',
                    border: '2px solid #e5e7eb',
                    borderRadius: '12px',
                    fontSize: '1rem',
                    transition: 'all 0.2s',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
                  }}
                  onFocus={(e) => {
                    e.currentTarget.style.borderColor = '#059669';
                    e.currentTarget.style.boxShadow = '0 0 0 3px rgba(5, 150, 105, 0.1)';
                  }}
                  onBlur={(e) => {
                    e.currentTarget.style.borderColor = '#e5e7eb';
                    e.currentTarget.style.boxShadow = '0 2px 4px rgba(0,0,0,0.05)';
                  }}
                  placeholder="email@example.com"
                />
              </div>
            </div>

            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end', marginTop: '28px' }}>
              <button
                onClick={() => setShowNewClientModal(false)}
                style={{
                  padding: '12px 24px',
                  background: '#f3f4f6',
                  color: '#374151',
                  border: 'none',
                  borderRadius: '12px',
                  cursor: 'pointer',
                  fontWeight: '600',
                  fontSize: '0.95rem',
                  transition: 'all 0.2s'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = '#e5e7eb';
                  e.currentTarget.style.transform = 'translateY(-2px)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = '#f3f4f6';
                  e.currentTarget.style.transform = 'translateY(0)';
                }}
              >
                {language === 'ar' ? 'إلغاء' : 'Cancel'}
              </button>
              <button
                onClick={handleCreateClient}
                disabled={creatingClient || !newClientData.name || !newClientData.phone}
                style={{
                  padding: '12px 28px',
                  background: creatingClient || !newClientData.name || !newClientData.phone
                    ? '#9ca3af'
                    : 'linear-gradient(135deg, #059669 0%, #047857 100%)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '12px',
                  cursor: creatingClient || !newClientData.name || !newClientData.phone ? 'not-allowed' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                  fontWeight: '600',
                  fontSize: '1rem',
                  boxShadow: creatingClient || !newClientData.name || !newClientData.phone
                    ? 'none'
                    : '0 4px 12px rgba(5, 150, 105, 0.3)',
                  transition: 'all 0.2s'
                }}
                onMouseEnter={(e) => {
                  if (!creatingClient && newClientData.name && newClientData.phone) {
                    e.currentTarget.style.transform = 'translateY(-2px)';
                    e.currentTarget.style.boxShadow = '0 6px 16px rgba(5, 150, 105, 0.4)';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!creatingClient && newClientData.name && newClientData.phone) {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = '0 4px 12px rgba(5, 150, 105, 0.3)';
                  }
                }}
              >
                {creatingClient ? (
                  <>
                    <FaSpinner style={{ animation: 'spin 1s linear infinite' }} />
                    {language === 'ar' ? 'جاري الإضافة...' : 'Adding...'}
                  </>
                ) : (
                  <>
                    <FaUserPlus />
                    {language === 'ar' ? 'إضافة العميل' : 'Add Client'}
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Cancel Deposit Request Modal */}
      {showCancelDepositModal && cancellingDeposit && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '16px',
            padding: '30px',
            width: '90%',
            maxWidth: '450px',
            maxHeight: '90vh',
            overflowY: 'auto'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h3 style={{ margin: 0, color: '#dc2626', display: 'flex', alignItems: 'center', gap: '10px' }}>
                <FaExclamationTriangle />
                {language === 'ar' ? 'طلب إلغاء العربون' : 'Request Deposit Cancellation'}
              </h3>
              <button
                onClick={() => setShowCancelDepositModal(false)}
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#6b7280', fontSize: '20px' }}
              >
                <FaTimes />
              </button>
            </div>

            <div style={{ 
              padding: '15px', 
              backgroundColor: '#fef2f2', 
              borderRadius: '8px', 
              marginBottom: '20px',
              border: '1px solid #fecaca'
            }}>
              <div style={{ fontWeight: '600', color: '#dc2626', marginBottom: '8px' }}>
                {language === 'ar' ? 'تحذير: سيتم:' : 'Warning: This will:'}
              </div>
              <ul style={{ margin: 0, paddingRight: language === 'ar' ? '20px' : '0', paddingLeft: language === 'ar' ? '0' : '20px', color: '#7f1d1d', fontSize: '14px' }}>
                <li>{language === 'ar' ? 'إلغاء العربون وتغيير حالته' : 'Cancel the deposit'}</li>
                <li>{language === 'ar' ? 'إرجاع حالة الوحدة إلى "شاغرة"' : 'Revert unit status to "vacant"'}</li>
                <li>{language === 'ar' ? 'إلغاء تذكيرات إغلاق الإعلان' : 'Cancel ad closure reminders'}</li>
                <li>{language === 'ar' ? 'إنشاء مهمة إعادة إعلان (إذا تم إغلاق الإعلان)' : 'Create re-advertise task (if ad was closed)'}</li>
              </ul>
            </div>

            <div style={{ 
              padding: '15px', 
              backgroundColor: '#f9fafb', 
              borderRadius: '8px', 
              marginBottom: '20px',
              fontSize: '14px'
            }}>
              <div style={{ marginBottom: '5px' }}>
                <strong>{language === 'ar' ? 'الوحدة:' : 'Unit:'}</strong> {cancellingDeposit.unit_number}
              </div>
              <div style={{ marginBottom: '5px' }}>
                <strong>{language === 'ar' ? 'المبلغ:' : 'Amount:'}</strong> {Number(cancellingDeposit.amount).toLocaleString()} {language === 'ar' ? 'ر.س' : 'SAR'}
              </div>
              <div>
                <strong>{language === 'ar' ? 'العميل:' : 'Client:'}</strong> {cancellingDeposit.client_name}
              </div>
            </div>
            
            <form onSubmit={handleRequestCancellation}>
              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', marginBottom: '5px', color: '#374151', fontWeight: '500' }}>
                  {language === 'ar' ? 'سبب الإلغاء * (مطلوب)' : 'Cancellation Reason * (required)'}
                </label>
                <textarea
                  value={cancellationReason}
                  onChange={(e) => setCancellationReason(e.target.value)}
                  required
                  rows={4}
                  style={{
                    width: '100%',
                    padding: '10px',
                    borderRadius: '8px',
                    border: '1px solid #d1d5db',
                    boxSizing: 'border-box',
                    fontSize: '14px',
                    fontFamily: 'inherit'
                  }}
                  placeholder={language === 'ar' 
                    ? 'يرجى كتابة سبب الإلغاء بالتفصيل (10 أحرف على الأقل)...' 
                    : 'Please provide detailed cancellation reason (minimum 10 characters)...'}
                />
                <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '5px' }}>
                  {language === 'ar' ? 'الحد الأدنى: 10 أحرف' : 'Minimum: 10 characters'}
                </div>
              </div>

              <div style={{ 
                display: 'flex', 
                gap: '10px', 
                justifyContent: 'flex-end'
              }}>
                <button
                  type="button"
                  onClick={() => setShowCancelDepositModal(false)}
                  style={{
                    padding: '10px 20px',
                    backgroundColor: '#f3f4f6',
                    color: '#374151',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: 'pointer'
                  }}
                >
                  {language === 'ar' ? 'إلغاء' : 'Cancel'}
                </button>
                <button
                  type="submit"
                  disabled={submittingCancellation || !cancellationReason || cancellationReason.trim().length < 10}
                  style={{
                    padding: '10px 20px',
                    backgroundColor: submittingCancellation || !cancellationReason || cancellationReason.trim().length < 10 ? '#9ca3af' : '#dc2626',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: submittingCancellation || !cancellationReason || cancellationReason.trim().length < 10 ? 'not-allowed' : 'pointer',
                    fontWeight: '500',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px'
                  }}
                >
                  {submittingCancellation ? (
                    <>
                      <FaSpinner style={{ animation: 'spin 1s linear infinite' }} />
                      {language === 'ar' ? 'جاري الإرسال...' : 'Submitting...'}
                    </>
                  ) : (
                    <>
                      <FaExclamationTriangle />
                      {language === 'ar' ? 'إرسال طلب الإلغاء' : 'Submit Cancellation Request'}
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Deposit Modal */}
      {showDepositModal && selectedUnit && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '16px',
            padding: '30px',
            width: '90%',
            maxWidth: '450px',
            maxHeight: '90vh',
            overflowY: 'auto'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h3 style={{ margin: 0, color: '#1f2937', display: 'flex', alignItems: 'center', gap: '10px' }}>
                <FaMoneyBillWave style={{ color: '#2563eb' }} />
                {language === 'ar' ? 'تسجيل عربون' : 'Record Deposit'}
              </h3>
              <button
                onClick={() => setShowDepositModal(false)}
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#6b7280', fontSize: '20px' }}
              >
                <FaTimes />
              </button>
            </div>

            <div style={{ 
              padding: '12px', 
              backgroundColor: '#dbeafe', 
              borderRadius: '8px', 
              marginBottom: '20px',
              fontSize: '14px',
              color: '#1e40af'
            }}>
              <strong>{language === 'ar' ? 'الوحدة:' : 'Unit:'}</strong> {selectedUnit.unit_number}
              <div style={{ fontSize: '12px', marginTop: '4px', color: '#3b82f6' }}>
                {language === 'ar' 
                  ? 'سيتم تحويل حالة الوحدة إلى "محجوز" وإنشاء تذكير لإزالة الإعلان'
                  : 'Unit will be marked as "Reserved" and reminder created to remove ad'}
              </div>
            </div>
            
            <form onSubmit={handleDepositSubmit}>
              <div style={{ display: 'grid', gap: '15px' }}>
                <div>
                  <label style={{ display: 'block', marginBottom: '5px', color: '#374151', fontWeight: '500' }}>
                    {language === 'ar' ? 'اسم العميل *' : 'Client Name *'}
                  </label>
                  <input
                    type="text"
                    value={depositFormData.client_name}
                    onChange={(e) => setDepositFormData({...depositFormData, client_name: e.target.value})}
                    required
                    style={{
                      width: '100%',
                      padding: '10px',
                      borderRadius: '8px',
                      border: '1px solid #d1d5db',
                      boxSizing: 'border-box'
                    }}
                    placeholder={language === 'ar' ? 'مثال: أحمد محمد' : 'e.g. Ahmed Mohammed'}
                  />
                </div>

                <div>
                  <label style={{ display: 'block', marginBottom: '5px', color: '#374151', fontWeight: '500' }}>
                    {language === 'ar' ? 'رقم الهاتف *' : 'Phone Number *'}
                  </label>
                  <input
                    type="tel"
                    value={depositFormData.client_phone}
                    onChange={(e) => setDepositFormData({...depositFormData, client_phone: e.target.value})}
                    required
                    style={{
                      width: '100%',
                      padding: '10px',
                      borderRadius: '8px',
                      border: '1px solid #d1d5db',
                      boxSizing: 'border-box'
                    }}
                    placeholder="05xxxxxxxx"
                  />
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
                  <div>
                    <label style={{ display: 'block', marginBottom: '5px', color: '#374151', fontWeight: '500' }}>
                      {language === 'ar' ? 'المبلغ (ر.س) *' : 'Amount (SAR) *'}
                    </label>
                    <input
                      type="number"
                      value={depositFormData.amount}
                      onChange={(e) => setDepositFormData({...depositFormData, amount: e.target.value})}
                      required
                      style={{
                        width: '100%',
                        padding: '10px',
                        borderRadius: '8px',
                        border: '1px solid #d1d5db',
                        boxSizing: 'border-box'
                      }}
                      placeholder="2500"
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', marginBottom: '5px', color: '#374151', fontWeight: '500' }}>
                      {language === 'ar' ? 'طريقة الدفع' : 'Payment Method'}
                    </label>
                    <select
                      value={depositFormData.payment_method}
                      onChange={(e) => setDepositFormData({...depositFormData, payment_method: e.target.value})}
                      style={{
                        width: '100%',
                        padding: '10px',
                        borderRadius: '8px',
                        border: '1px solid #d1d5db'
                      }}
                    >
                      <option value="cash">{language === 'ar' ? 'نقدي' : 'Cash'}</option>
                      <option value="transfer">{language === 'ar' ? 'تحويل بنكي' : 'Bank Transfer'}</option>
                      <option value="check">{language === 'ar' ? 'شيك' : 'Check'}</option>
                    </select>
                  </div>
                </div>

                {/* Receipt Upload */}
                <div>
                  <label style={{ display: 'block', marginBottom: '5px', color: '#374151', fontWeight: '500' }}>
                    {language === 'ar' ? 'صورة الإيصال *' : 'Receipt Image *'}
                  </label>
                  <div style={{ 
                    border: `2px dashed ${depositFormData.receipt_preview ? '#16a34a' : '#d1d5db'}`,
                    borderRadius: '12px',
                    padding: '20px',
                    textAlign: 'center',
                    backgroundColor: depositFormData.receipt_preview ? '#f0fdf4' : '#f9fafb',
                    cursor: 'pointer',
                    transition: 'all 0.2s'
                  }}
                  onClick={() => document.getElementById('receipt-input-employee').click()}
                  >
                    <input
                      id="receipt-input-employee"
                      type="file"
                      accept="image/*"
                      onChange={handleReceiptUpload}
                      style={{ display: 'none' }}
                    />
                    {depositFormData.receipt_preview ? (
                      <div>
                        <img 
                          src={depositFormData.receipt_preview} 
                          alt="Receipt preview" 
                          style={{ maxWidth: '100%', maxHeight: '200px', borderRadius: '8px', marginBottom: '10px' }}
                        />
                        <p style={{ margin: 0, color: '#16a34a', fontSize: '0.9rem' }}>
                          {language === 'ar' ? '✓ تم رفع الصورة' : '✓ Image uploaded'}
                        </p>
                      </div>
                    ) : (
                      <div>
                        <FaPlus style={{ fontSize: '2rem', color: '#9ca3af', marginBottom: '10px' }} />
                        <p style={{ margin: 0, color: '#6b7280' }}>
                          {language === 'ar' ? 'اضغط لرفع صورة الإيصال' : 'Click to upload receipt image'}
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div style={{ display: 'flex', gap: '10px', marginTop: '20px' }}>
                <button
                  type="button"
                  onClick={() => setShowDepositModal(false)}
                  style={{
                    padding: '10px 20px',
                    backgroundColor: '#f3f4f6',
                    color: '#374151',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    flex: 1
                  }}
                >
                  {language === 'ar' ? 'إلغاء' : 'Cancel'}
                </button>
                <button
                  type="submit"
                  disabled={uploadingReceipt}
                  style={{
                    padding: '10px 20px',
                    backgroundColor: uploadingReceipt ? '#9ca3af' : '#f97316',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: uploadingReceipt ? 'not-allowed' : 'pointer',
                    fontWeight: '500',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '6px',
                    flex: 1
                  }}
                >
                  {uploadingReceipt ? (
                    <>
                      <FaSpinner style={{ animation: 'spin 1s linear infinite' }} />
                      {language === 'ar' ? 'جاري التسجيل...' : 'Recording...'}
                    </>
                  ) : (
                    <>
                      <FaMoneyBillWave />
                      {language === 'ar' ? 'تسجيل العربون' : 'Record Deposit'}
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Success Animation */}
      {/* View Listing Details Modal */}
      {showViewListingModal && (
        <div 
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.5)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 1000
          }}
          onClick={() => setShowViewListingModal(false)}
        >
          <div 
            style={{
              backgroundColor: 'white',
              borderRadius: '16px',
              padding: '30px',
              width: '90%',
              maxWidth: '600px',
              maxHeight: '90vh',
              overflowY: 'auto',
              boxShadow: '0 10px 40px rgba(0,0,0,0.2)'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {loadingListingDetails ? (
              <div style={{ textAlign: 'center', padding: '40px' }}>
                <FaSpinner style={{ fontSize: '2rem', color: '#f97316', animation: 'spin 1s linear infinite' }} />
                <p style={{ marginTop: '20px' }}>{language === 'ar' ? 'جاري التحميل...' : 'Loading...'}</p>
              </div>
            ) : viewingListing ? (
              <>
                <div style={{ 
                  display: 'flex', 
                  justifyContent: 'space-between', 
                  alignItems: 'center',
                  marginBottom: '20px'
                }}>
                  <h2 style={{ margin: 0, color: '#1f2937' }}>
                    {language === 'ar' ? 'تفاصيل الإعلان' : 'Listing Details'}
                  </h2>
                  <button
                    onClick={() => setShowViewListingModal(false)}
                    style={{
                      background: 'none',
                      border: 'none',
                      fontSize: '24px',
                      cursor: 'pointer',
                      color: '#6b7280',
                      padding: '0',
                      width: '30px',
                      height: '30px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                  >
                    ×
                  </button>
                </div>

                <div style={{ display: 'grid', gap: '20px' }}>
                  {/* Listing Info */}
                  <div>
                    <h3 style={{ margin: '0 0 15px 0', color: '#374151', fontSize: '1.1rem' }}>
                      {language === 'ar' ? 'معلومات الإعلان' : 'Listing Information'}
                    </h3>
                    <div style={{ display: 'grid', gap: '12px' }}>
                      <div>
                        <label style={{ display: 'block', marginBottom: '5px', color: '#6b7280', fontSize: '0.9rem' }}>
                          {language === 'ar' ? 'العنوان' : 'Title'}
                        </label>
                        <div style={{ padding: '10px', background: '#f9fafb', borderRadius: '8px', color: '#1f2937' }}>
                          {viewingListing.title || '-'}
                        </div>
                      </div>
                      
                      {viewingListing.description && (
                        <div>
                          <label style={{ display: 'block', marginBottom: '5px', color: '#6b7280', fontSize: '0.9rem' }}>
                            {language === 'ar' ? 'الوصف' : 'Description'}
                          </label>
                          <div style={{ padding: '10px', background: '#f9fafb', borderRadius: '8px', color: '#1f2937', whiteSpace: 'pre-wrap' }}>
                            {viewingListing.description}
                          </div>
                        </div>
                      )}
                      
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                        <div>
                          <label style={{ display: 'block', marginBottom: '5px', color: '#6b7280', fontSize: '0.9rem' }}>
                            {language === 'ar' ? 'المنصة' : 'Platform'}
                          </label>
                          <div style={{ padding: '10px', background: '#f9fafb', borderRadius: '8px', color: '#1f2937' }}>
                            {viewingListing.platform || '-'}
                          </div>
                        </div>
                        
                        <div>
                          <label style={{ display: 'block', marginBottom: '5px', color: '#6b7280', fontSize: '0.9rem' }}>
                            {language === 'ar' ? 'السعر' : 'Price'}
                          </label>
                          <div style={{ padding: '10px', background: '#f9fafb', borderRadius: '8px', color: '#1f2937' }}>
                            {viewingListing.price ? `${Number(viewingListing.price).toLocaleString()} ${language === 'ar' ? 'ر.س' : 'SAR'}` : '-'}
                          </div>
                        </div>
                      </div>
                      
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                        <div>
                          <label style={{ display: 'block', marginBottom: '5px', color: '#6b7280', fontSize: '0.9rem' }}>
                            {language === 'ar' ? 'الحالة' : 'Status'}
                          </label>
                          <div style={{ padding: '10px', background: '#f9fafb', borderRadius: '8px', color: '#1f2937' }}>
                            {viewingListing.status === 'ad_posted' ? (language === 'ar' ? 'تم النشر' : 'Posted') :
                             viewingListing.status === 'active' ? (language === 'ar' ? 'نشط' : 'Active') :
                             viewingListing.status === 'pending' ? (language === 'ar' ? 'قيد الانتظار' : 'Pending') :
                             viewingListing.status || '-'}
                          </div>
                        </div>
                        
                        {viewingListing.posted_at && (
                          <div>
                            <label style={{ display: 'block', marginBottom: '5px', color: '#6b7280', fontSize: '0.9rem' }}>
                              {language === 'ar' ? 'تاريخ النشر' : 'Posted At'}
                            </label>
                            <div style={{ padding: '10px', background: '#f9fafb', borderRadius: '8px', color: '#1f2937' }}>
                              {new Date(viewingListing.posted_at).toLocaleString(language === 'ar' ? 'ar-SA' : 'en-US')}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* External URL Section */}
                  <div>
                    <h3 style={{ margin: '0 0 15px 0', color: '#374151', fontSize: '1.1rem' }}>
                      {language === 'ar' ? 'رابط الإعلان' : 'Ad URL'}
                    </h3>
                    <div style={{ display: 'flex', gap: '10px', alignItems: 'flex-start' }}>
                      <input
                        type="url"
                        value={editingListingUrl}
                        onChange={(e) => setEditingListingUrl(e.target.value)}
                        placeholder={language === 'ar' ? 'https://haraj.com.sa/...' : 'https://haraj.com.sa/...'}
                        style={{
                          flex: 1,
                          padding: '12px',
                          border: '2px solid #e5e7eb',
                          borderRadius: '8px',
                          fontSize: '0.95rem'
                        }}
                      />
                      <button
                        onClick={handleSaveListingUrl}
                        disabled={savingListingUrl}
                        style={{
                          padding: '12px 24px',
                          background: savingListingUrl ? '#9ca3af' : '#059669',
                          color: 'white',
                          border: 'none',
                          borderRadius: '8px',
                          cursor: savingListingUrl ? 'not-allowed' : 'pointer',
                          fontWeight: '600',
                          fontSize: '0.95rem'
                        }}
                      >
                        {savingListingUrl 
                          ? (language === 'ar' ? 'جاري الحفظ...' : 'Saving...')
                          : (language === 'ar' ? 'حفظ' : 'Save')
                        }
                      </button>
                    </div>
                    
                    {viewingListing.external_url && viewingListing.external_url.trim() && (
                      <div style={{ marginTop: '15px' }}>
                        <a
                          href={viewingListing.external_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={(e) => e.stopPropagation()}
                          style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '8px',
                            padding: '10px 16px',
                            background: '#dbeafe',
                            color: '#2563eb',
                            borderRadius: '8px',
                            textDecoration: 'none',
                            fontWeight: '600',
                            fontSize: '0.95rem'
                          }}
                        >
                          <FaExternalLinkAlt />
                          {language === 'ar' ? 'فتح الإعلان في نافذة جديدة' : 'Open Ad in New Tab'}
                        </a>
                      </div>
                    )}
                  </div>
                </div>

                <div style={{ marginTop: '25px', display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
                  <button
                    onClick={() => setShowViewListingModal(false)}
                    style={{
                      padding: '10px 20px',
                      background: '#f3f4f6',
                      color: '#374151',
                      border: 'none',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      fontWeight: '600'
                    }}
                  >
                    {language === 'ar' ? 'إغلاق' : 'Close'}
                  </button>
                </div>
              </>
            ) : null}
          </div>
        </div>
      )}

      <SuccessAnimation 
        show={showSuccess} 
        onComplete={() => setShowSuccess(false)}
        message={successMessage}
      />
    </div>
  );
};

export default EmployeeDashboard;

